import { DSA_INDEXER_BYTES, DSV4_FP8_BYTES, SPARSE_MLA_FP8_BYTES, convState, csa2State, dsv4State, gdnState, gqa, kdaState, mamba2State, mla, type KvGroup } from "./core.ts";
import type { HwFormat } from "./hardware.ts";

import AUTO from "../../data/models-auto.json" with { type: "json" };

// ── Modèles ─────────────────────────────────────────────────────────────────

export type ModelPreset = {
  id: string;
  label: string;
  group: string;
  /** Milliards de paramètres dans le checkpoint officiel (vision / MTP inclus s'ils y sont). */
  params: number;
  activeParams?: number;
  hidden: number;
  layers: number;
  vocab: number;
  maxContext: number;
  kv: KvGroup[];
  stateBytesPerSeq?: number;
  /**
   * Checkpoint pré-quantifié : octets exacts des fichiers safetensors, et le support
   * matériel qu'il demande pour calculer nativement (cœurs FP8 / FP4).
   */
  native?: { label: string; bytes: number; needs?: HwFormat;
    /** Dépôt du checkpoint natif quand ce n'est pas `repo` (Meta publie Muse Spark Open en BF16 et en NVFP4,
     *  dans deux dépôts) : la commande de déploiement le sert quand le visiteur choisit ce format. */
    repo?: string };
  /** Cache KV en FP8 par défaut : le checkpoint fournit des échelles calibrées, ou vLLM impose ce
   *  format (DeepSeek V4 et V4.1 sur GPU NVIDIA : « fp8_ds_mla », même avec --kv-cache-dtype auto). */
  fp8Kv?: boolean;
  /** Embeddings d'entrée et tête de sortie partagés (tie_word_embeddings) : un seul tableau
   *  vocabulaire × largeur. Absent : deux tableaux, le cas des grands modèles. */
  tiedEmbeddings?: boolean;
  /**
   * Recensement des tenseurs du checkpoint officiel (en-têtes safetensors, voir
   * scripts/sources/safetensors.ts), en milliards de paramètres : couches de prédiction
   * multi-token (vLLM et llama.cpp ne les chargent qu'en décodage spéculatif), encodeur de vision
   * ou d'audio (fichier mmproj à part en GGUF), experts routés d'un MoE (hors experts partagés),
   * tables lues ligne par ligne (n-gram de Qwen, Engram de DeepSeek : en mémoire, peu lues),
   * matrices dont la largeur d'entrée n'est pas un multiple de 256 (odd : llama.cpp leur donne un
   * format plus large, voir GGUF_FALLBACK).
   */
  parts?: { mtp?: number; vision?: number; experts?: number; lookup?: number; odd?: number };
  /** Octets des couches MTP dans les fichiers officiels (recensement), retirés du checkpoint natif. */
  mtpBytes?: number;
  /** Bits des experts dans les fichiers GGUF quand llama.cpp les garde dans leur format natif
   *  (gpt-oss : MXFP4 à 4,25 bits, quelle que soit la quantification annoncée). */
  ggufExpertBits?: number;
  /** Contexte au-delà de config.json par YaRN (Qwen3 d'origine : 40 960 dans la config, 131 072 avec un facteur 4
   *  sur 32 768, comme le disent leurs fiches officielles). maxContext est alors le contexte étendu ; au-delà de
   *  `native`, la commande active YaRN (deploy.ts). */
  yarn?: { factor: number; original: number; native: number };
  /** MoE : experts routés par couche, combien chaque token en active, et la dimension interne d'un expert
   *  (moe_intermediate_size, config.json ; sert à l'arrondi des MoE FP4 de vLLM, voir reserve.ts). */
  routing?: { experts: number; active: number; ffn?: number };
  arch: string;
  /** Dépôt Hugging Face ; vide tant que les poids ne sont pas publiés (voir `preview`). */
  repo: string;
  /** Sortie (AAAA-MM-JJ) : création du dépôt Hugging Face, ou annonce pour une preview. */
  released?: string;
  /** Organisation Hugging Face, quand le dépôt n'existe pas encore (preview) : voir labOf (catalog.ts). */
  org?: string;
  /**
   * Modèle annoncé dont les poids ne sont pas encore publiés : chiffres provisoires,
   * expliqués par `note` sur le calculateur et la page du modèle. `url` : fiche officielle
   * (à la place de la page Hugging Face) ; `api` : id OpenRouter, qui n'a pas encore de
   * hugging_face_id pour ce modèle (voir API_ONLY dans fetch-gpu-prices.mjs).
   */
  preview?: { note: string; url: string; api: string };
  /** Dépôt au seul format de Mistral (params.json, consolidated-*.safetensors, pas de config.json) : voir deployCommand. */
  mistralFormat?: true;
};

const VERIFIED_MODELS: ModelPreset[] = [
  // Dense, attention complète sur toutes les couches.
  {
    id: "llama-3.1-8b", label: "Llama 3.1 8B", group: "Dense",
    params: 8.03, hidden: 4096, layers: 32, vocab: 128256, maxContext: 131072,
    kv: [gqa(32, 8, 128)],
    arch: "32 layers · GQA, 8 KV heads × 128",
    repo: "meta-llama/Llama-3.1-8B-Instruct", released: "2024-07-18",
  },
  // Modèles par défaut des familles Ollama les plus téléchargées (tag « latest », ollama.com, 20 octobre 2026) : chaque
  // ligne de « Popular on Ollama » mène à une fiche. config.json lus sur les copies unsloth (même total de paramètres
  // que les dépôts officiels, protégés) ; en-têtes safetensors pour la vision et les embeddings partagés.
  {
    id: "llama-3-8b", label: "Llama 3 8B", group: "Dense",
    params: 8.03, hidden: 4096, layers: 32, vocab: 128256, maxContext: 8192,
    kv: [gqa(32, 8, 128)],
    arch: "32 layers · GQA, 8 KV heads × 128",
    repo: "meta-llama/Meta-Llama-3-8B-Instruct", released: "2024-04-18",
  },
  {
    id: "llama-3.2-3b", label: "Llama 3.2 3B", group: "Dense",
    params: 3.21, hidden: 3072, layers: 28, vocab: 128256, maxContext: 131072, tiedEmbeddings: true,
    kv: [gqa(28, 8, 128)],
    arch: "28 layers · GQA, 8 KV heads × 128",
    repo: "meta-llama/Llama-3.2-3B-Instruct", released: "2024-09-25",
  },
  {
    // Nanbeige LLM Lab (BOSS Zhipin) : architecture Llama (config.json), GQA 8 têtes KV × 128 sur les 52 couches, sans
    // fenêtre glissante. README : 512K tokens (524 288, rope_theta 1,2 × 10⁸ sans mise à l'échelle). 33,12 B (en-têtes
    // safetensors) ; toutes les largeurs sont des multiples de 256.
    id: "nanbeige4.3-33b", label: "Nanbeige4.3 33B", group: "Dense",
    params: 33.12, hidden: 7168, layers: 52, vocab: 166912, maxContext: 524288,
    kv: [gqa(52, 8, 128)],
    arch: "52 layers · GQA, 8 KV heads × 128",
    repo: "Nanbeige/Nanbeige4.3-33B", released: "2026-10-24",
  },
  {
    // IBM (README, config.json « granite ») : denses, GQA 8 têtes KV sur toutes les couches, 128K natifs (le README annonce
    // une extension à 512K sans en donner la méthode : pas reprise). Sortis le 25 août 2026, Apache 2.0.
    id: "granite-4.2-30b", label: "Granite 4.2 30B", group: "Dense",
    params: 29.28, hidden: 4096, layers: 64, vocab: 100352, maxContext: 131072,
    kv: [gqa(64, 8, 128)],
    arch: "64 layers · GQA, 8 KV heads × 128",
    repo: "ibm-granite/granite-4.2-30b", released: "2026-08-25",
  },
  {
    id: "granite-4.2-8b", label: "Granite 4.2 8B", group: "Dense",
    params: 8.79, hidden: 4096, layers: 40, vocab: 100352, maxContext: 131072,
    kv: [gqa(40, 8, 128)],
    arch: "40 layers · GQA, 8 KV heads × 128",
    repo: "ibm-granite/granite-4.2-8b", released: "2026-08-25",
  },
  {
    id: "granite-4.2-3b", label: "Granite 4.2 3B", group: "Dense",
    params: 3.66, hidden: 2560, layers: 40, vocab: 100352, maxContext: 131072,
    kv: [gqa(40, 8, 64)],
    arch: "40 layers · GQA, 8 KV heads × 64",
    repo: "ibm-granite/granite-4.2-3b", released: "2026-08-25",
  },
  {
    // OpenBMB (README, config.json « llama ») : GQA 2 têtes KV × 128, 128K. 2 516 756 480 paramètres (README).
    id: "minicpm5-2b", label: "MiniCPM5 2B", group: "Dense",
    params: 2.52, hidden: 2048, layers: 42, vocab: 130560, maxContext: 131072,
    kv: [gqa(42, 2, 128)],
    arch: "42 layers · GQA, 2 KV heads × 128",
    repo: "openbmb/MiniCPM5-2B", released: "2026-09-06",
  },
  {
    id: "minicpm5-1b", label: "MiniCPM5 1B", group: "Dense",
    params: 1.08, hidden: 1536, layers: 24, vocab: 130560, maxContext: 131072,
    kv: [gqa(24, 2, 128)],
    arch: "24 layers · GQA, 2 KV heads × 128",
    repo: "openbmb/MiniCPM5-1B", released: "2026-05-21",
  },
  {
    // Mistral AI (README, config.json « ministral3 », en-têtes safetensors) : denses, GQA 8 têtes KV × 128 sur toutes les
    // couches, encodeur de vision de 0,43 B ; 256K (YaRN). Checkpoints FP8 (les versions BF16 sont des dépôts à part).
    // Apache 2.0, sortis le 2 décembre 2025 (Mistral 3).
    id: "ministral-3-14b", label: "Ministral 3 14B", group: "Dense",
    params: 13.95, hidden: 5120, layers: 40, vocab: 131072, maxContext: 262144,
    parts: { vision: 0.439 },
    kv: [gqa(40, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 15_726_582_880 },
    arch: "40 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Ministral-3-14B-Instruct-2512", released: "2025-12-02",
  },
  {
    id: "ministral-3-8b", label: "Ministral 3 8B", group: "Dense",
    params: 8.92, hidden: 4096, layers: 34, vocab: 131072, maxContext: 262144,
    parts: { vision: 0.428 },
    kv: [gqa(34, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 10_420_523_960 },
    arch: "34 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Ministral-3-8B-Instruct-2512", released: "2025-12-02",
  },
  {
    id: "ministral-3-3b", label: "Ministral 3 3B", group: "Dense",
    params: 3.85, hidden: 3072, layers: 26, vocab: 131072, maxContext: 262144, tiedEmbeddings: true,
    parts: { vision: 0.42 },
    kv: [gqa(26, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 4_671_990_488 },
    arch: "26 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Ministral-3-3B-Instruct-2512", released: "2025-12-02",
  },
  {
    // Mistral AI (README, config.json) : dense, 88 couches, GQA 8 × 128 (l'architecture de Mistral Large 2), 256K (YaRN),
    // checkpoint FP8. Licence propre (Modified MIT). Sorti le 9 décembre 2025.
    id: "devstral-2", label: "Devstral 2 123B", group: "Dense",
    params: 125.03, hidden: 12288, layers: 88, vocab: 131072, maxContext: 262144,
    kv: [gqa(88, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 128_249_391_520 },
    arch: "88 layers · GQA, 8 KV heads × 128",
    repo: "mistralai/Devstral-2-123B-Instruct-2512", released: "2025-12-09",
  },
  {
    // 40 couches, GQA 8 × 128, encodeur de vision de 0,44 B ; 256K (README ; config.json va jusqu'à 384K). FP8. Apache 2.0.
    id: "devstral-small-2", label: "Devstral Small 2 24B", group: "Dense",
    params: 24.01, hidden: 5120, layers: 40, vocab: 131072, maxContext: 262144,
    parts: { vision: 0.439 },
    kv: [gqa(40, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 25_792_912_480 },
    arch: "40 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Devstral-Small-2-24B-Instruct-2512", released: "2025-12-09",
  },
  {
    // Dense 128 B, 88 couches, GQA 8 × 128, encodeur de vision de 2,7 B ; 256K (README). FP8. Annonce du 22 mai 2026
    // (mistral.ai/news/vibe-remote-agents-mistral-medium-3-5), Modified MIT.
    id: "mistral-medium-3.5", label: "Mistral Medium 3.5 128B", group: "Dense",
    params: 127.7, hidden: 12288, layers: 88, vocab: 131072, maxContext: 262144,
    parts: { vision: 2.678 },
    kv: [gqa(88, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 133_605_834_656 },
    arch: "88 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Mistral-Medium-3.5-128B", released: "2026-05-22",
  },
  {
    // Magistral Small 1.2 : Mistral Small 3.2 (même architecture) entraîné au raisonnement, encodeur de vision ; 128K (README).
    // Apache 2.0, sorti le 17 septembre 2025.
    id: "magistral-small", label: "Magistral Small 1.2 24B", group: "Dense",
    params: 24.01, hidden: 5120, layers: 40, vocab: 131072, maxContext: 131072,
    parts: { vision: 0.439 },
    kv: [gqa(40, 8, 128)],
    arch: "40 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Magistral-Small-2509", released: "2025-09-17",
  },
  {
    // Mistral Small 3 (2501) : texte seul, 40 couches, GQA 8 × 128, 32K. Apache 2.0, sorti le 30 janvier 2025.
    id: "mistral-small-3", label: "Mistral Small 3 24B", group: "Dense",
    params: 23.57, hidden: 5120, layers: 40, vocab: 131072, maxContext: 32768,
    kv: [gqa(40, 8, 128)],
    arch: "40 layers · GQA, 8 KV heads × 128",
    repo: "mistralai/Mistral-Small-24B-Instruct-2501", released: "2025-01-30",
  },
  {
    // Mistral Large 2.1 (2411) : 88 couches, GQA 8 × 128, 128K. Mistral Research License. Sorti le 18 novembre 2024.
    id: "mistral-large-2411", label: "Mistral Large 2411 123B", group: "Dense",
    params: 122.61, hidden: 12288, layers: 88, vocab: 32768, maxContext: 131072,
    kv: [gqa(88, 8, 128)],
    arch: "88 layers · GQA, 8 KV heads × 128",
    repo: "mistralai/Mistral-Large-Instruct-2411", released: "2024-11-18",
  },
  {
    // Pixtral Large : le décodeur de Mistral Large 2 et un encodeur de vision de 1,5 B (params.json, en-têtes au format
    // Mistral) ; 128K. Mistral Research License. Sorti le 18 novembre 2024.
    id: "pixtral-large", label: "Pixtral Large 124B", group: "Dense",
    params: 124.14, hidden: 12288, layers: 88, vocab: 32768, maxContext: 131072,
    parts: { vision: 1.525 },
    kv: [gqa(88, 8, 128)],
    arch: "88 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Pixtral-Large-Instruct-2411", released: "2024-11-18", mistralFormat: true,
  },
  {
    // Codestral 22B : 56 couches, GQA 8 × 128, 32K. Mistral Non-Production License. Sorti le 29 mai 2024.
    id: "codestral-22b", label: "Codestral 22B", group: "Dense",
    params: 22.25, hidden: 6144, layers: 56, vocab: 32768, maxContext: 32768,
    kv: [gqa(56, 8, 128)],
    arch: "56 layers · GQA, 8 KV heads × 128",
    repo: "mistralai/Codestral-22B-v0.1", released: "2024-05-29",
  },
  {
    // Mistral Small 2409 (22 B) : 56 couches, GQA 8 × 128, 32K. Mistral Research License. Sorti le 17 septembre 2024.
    id: "mistral-small-2409", label: "Mistral Small 2409 22B", group: "Dense",
    params: 22.25, hidden: 6144, layers: 56, vocab: 32768, maxContext: 32768,
    kv: [gqa(56, 8, 128)],
    arch: "56 layers · GQA, 8 KV heads × 128",
    repo: "mistralai/Mistral-Small-Instruct-2409", released: "2024-09-17",
  },
  {
    // Pixtral 12B : décodeur de Mistral Nemo (40 couches, GQA 8 × 128) et encodeur de vision de 0,44 B (params.json, en-têtes au
    // format Mistral) ; 128K. Apache 2.0, sorti le 11 septembre 2024.
    id: "pixtral-12b", label: "Pixtral 12B", group: "Dense",
    params: 12.68, hidden: 5120, layers: 40, vocab: 131072, maxContext: 131072,
    parts: { vision: 0.435 },
    kv: [gqa(40, 8, 128)],
    arch: "40 layers · GQA, 8 KV heads × 128 · vision encoder",
    repo: "mistralai/Pixtral-12B-2409", released: "2024-09-11", mistralFormat: true,
  },
  {
    // NVIDIA (README, config.json, en-têtes safetensors) : lecteur de documents, encodeur de vision C-RADIOv4-SO400M (0,435 B)
    // et décodeur mBART de 10 couches qui lit l'image comme préfixe causal (pas d'attention croisée) : GQA 8 têtes KV × 64,
    // 16 384 tokens image comprise. Checkpoint en FP32 (2,5 Go), servi en BF16.
    id: "nemotron-3.5-parse", label: "Nemotron 3.5 Parse", group: "Dense",
    params: 0.63, hidden: 1024, layers: 10, vocab: 72512, maxContext: 16384, tiedEmbeddings: true,
    parts: { vision: 0.435 },
    kv: [gqa(10, 8, 64)],
    arch: "Document parser · C-RADIOv4 vision encoder + 10-layer decoder (GQA, 8 KV heads × 64), image as a causal prefix",
    repo: "nvidia/Nemotron-3.5-Parse", released: "2026-09-03",
  },
  {
    // Une couche sur deux en fenêtre de 4 096 tokens (modeling_gemma2.py), contexte de 8 192.
    id: "gemma-2-9b", label: "Gemma 2 9B", group: "Sliding-window attention",
    params: 9.24, hidden: 3584, layers: 42, vocab: 256000, maxContext: 8192, tiedEmbeddings: true,
    kv: [gqa(21, 8, 256), gqa(21, 8, 256, 4096)],
    arch: "21 full-attention layers + 21 sliding-window layers (4096 tokens), GQA 8 KV × 256",
    repo: "google/gemma-2-9b-it", released: "2024-06-27",
  },
  {
    // Une couche globale sur six, les autres en fenêtre de 1 024 tokens (modeling_gemma3.py) ; vision SigLIP de 0,42 B.
    id: "gemma-3-4b", label: "Gemma 3 4B", group: "Sliding-window attention",
    params: 4.30, hidden: 2560, layers: 34, vocab: 262208, maxContext: 131072, tiedEmbeddings: true,
    parts: { vision: 0.42 },
    kv: [gqa(5, 4, 256), gqa(29, 4, 256, 1024)],
    arch: "5 full-attention layers + 29 sliding-window layers (1024 tokens), GQA 4 KV × 256 · vision encoder",
    repo: "google/gemma-3-4b-it", released: "2025-03-12",
  },
  {
    // LLaVA 1.6 (LLaVA-NeXT) : Mistral 7B (32 couches, 8 têtes KV × 128, contexte 32K) et l'encodeur CLIP ViT-L (0,32 B).
    id: "llava-1.6-mistral-7b", label: "LLaVA 1.6 Mistral 7B", group: "Dense",
    params: 7.57, hidden: 4096, layers: 32, vocab: 32064, maxContext: 32768,
    parts: { vision: 0.324 },
    kv: [gqa(32, 8, 128)],
    arch: "32 layers · GQA, 8 KV heads × 128 · CLIP vision encoder",
    repo: "llava-hf/llava-v1.6-mistral-7b-hf", released: "2024-02-20",
  },
  {
    id: "mistral-small-3.2", label: "Mistral Small 3.2 24B", group: "Dense",
    params: 24.01, hidden: 5120, layers: 40, vocab: 131072, maxContext: 131072,
    parts: { vision: 0.439 },
    kv: [gqa(40, 8, 128)],
    arch: "40 layers · GQA, 8 KV heads × 128 · checkpoint stored in FP32, served in BF16",
    repo: "mistralai/Mistral-Small-3.2-24B-Instruct-2506", released: "2025-06-19",
  },
  {
    id: "qwen3-32b", label: "Qwen3 32B", group: "Dense",
    params: 32.76, hidden: 5120, layers: 64, vocab: 151936, maxContext: 131072, yarn: { factor: 4, original: 32768, native: 40960 },
    kv: [gqa(64, 8, 128)],
    arch: "64 layers · GQA, 8 KV heads × 128 · 32K native, 128K with YaRN",
    repo: "Qwen/Qwen3-32B", released: "2025-04-27",
  },
  {
    id: "llama-3.3-70b", label: "Llama 3.3 70B", group: "Dense",
    params: 70.55, hidden: 8192, layers: 80, vocab: 128256, maxContext: 131072,
    kv: [gqa(80, 8, 128)],
    arch: "80 layers · GQA, 8 KV heads × 128",
    repo: "meta-llama/Llama-3.3-70B-Instruct", released: "2024-11-26",
  },
  {
    // Config d'après la table 3 du papier Llama 3 : 8 têtes KV (et non 16).
    id: "llama-3.1-405b", label: "Llama 3.1 405B", group: "Dense",
    params: 405.85, hidden: 16384, layers: 126, vocab: 128256, maxContext: 131072,
    kv: [gqa(126, 8, 128)],
    arch: "126 layers · GQA, 8 KV heads × 128",
    repo: "meta-llama/Llama-3.1-405B", released: "2024-07-16",
  },
  {
    // Tencent Hunyuan (README, config.json, en-têtes safetensors) : modèle qui écrit la mémoire à long terme
    // des frameworks d'agents (Mem0, MemOS) ; « Memory » est son rôle, pas un mécanisme du modèle. GQA complète
    // sur 48 couches (4 têtes KV × 128), une couche dense puis 47 MoE : 128 experts de 768 (expert_hidden_dim ;
    // moe_intermediate_size dit 1 536 mais ne sert pas) dont 8 actifs, plus un partagé. Testé avec vLLM 0.31.0.
    id: "hy-memory-30b-a3b", label: "Hy-Memory 30B-A3B (MoE)", group: "Mixture of experts",
    params: 30.06, activeParams: 3, hidden: 2048, layers: 48, vocab: 120832, maxContext: 262144,
    parts: { experts: 28.387 }, routing: { experts: 128, active: 8, ffn: 768 },
    kv: [gqa(48, 4, 128)],
    arch: "MoE 128 experts + 1 shared · 48 layers, full attention (GQA, 4 KV heads × 128)",
    repo: "tencent/Hy-Memory-30B-A3B", released: "2026-10-15",
  },
  {
    // modeling_kimi_vl.py : décodeur DeepSeek-V3 (Moonlight-16B-A3B), MLA (512 + 64) sur les 27 couches, une couche
    // dense puis 26 MoE de 64 experts dont 6 actifs, plus 2 partagés ; encodeur MoonViT (0,45 B avec le projecteur).
    // Recensement : les down_proj des experts (largeur 1 408) sont « odd » pour llama.cpp. README : 16 B, 3 B actifs, 128K.
    id: "kimi-vl-a3b-instruct", label: "Kimi VL A3B Instruct (MoE)", group: "Mixture of experts",
    params: 16.41, activeParams: 3, hidden: 2048, layers: 27, vocab: 163840, maxContext: 131072,
    parts: { vision: 0.448, experts: 14.395, odd: 4.798 }, routing: { experts: 64, active: 6, ffn: 1408 },
    kv: [mla(27, 512, 64)],
    arch: "MoE 64 experts + 2 shared · MLA (512 + 64 latent) on 27 layers · MoonViT vision encoder",
    repo: "moonshotai/Kimi-VL-A3B-Instruct", released: "2025-04-09",
  },
  {
    // Mistral AI (config.json « mixtral ») : 32 couches, 8 têtes KV × 128, 8 experts dont 2 actifs ; 46,7 B dont 12,9 B
    // actifs, 32K. Apache 2.0, sorti le 11 décembre 2023.
    id: "mixtral-8x7b", label: "Mixtral 8x7B (MoE)", group: "Mixture of experts",
    params: 46.7, activeParams: 12.9, hidden: 4096, layers: 32, vocab: 32000, maxContext: 32768,
    parts: { experts: 45.097 }, routing: { experts: 8, active: 2, ffn: 14336 },
    kv: [gqa(32, 8, 128)],
    arch: "MoE 8 experts (2 active) · 32 layers, GQA 8 KV heads × 128",
    repo: "mistralai/Mixtral-8x7B-Instruct-v0.1", released: "2023-12-11",
  },
  {
    // Baidu : DeepEncoder de DeepSeek-OCR (SAM ViT-B + CLIP-L + projecteur, 0,40 B aux en-têtes safetensors) et décodeur
    // DeepSeek-V2 sans MLA, MoE de 64 experts dont 6 actifs plus 2 partagés (0,57 B actifs, les 570M de DeepSeek-OCR).
    // Fenêtre glissante de 128 tokens sur les 12 couches (sliding_window), 32K de contexte.
    id: "unlimited-ocr", label: "Unlimited-OCR (MoE)", group: "Sliding-window attention",
    params: 3.34, activeParams: 0.57, hidden: 1280, layers: 12, vocab: 129280, maxContext: 32768,
    parts: { vision: 0.401, experts: 2.422, odd: 0.816 }, routing: { experts: 64, active: 6, ffn: 896 },
    kv: [gqa(12, 10, 128, 128)],
    arch: "MoE 64 experts + 2 shared · 12 sliding-window layers (128 tokens, 10 KV × 128) · DeepEncoder vision (SAM + CLIP)",
    repo: "baidu/Unlimited-OCR", released: "2026-06-19",
  },
  {
    // Unlimited-OCR 2 (Baidu, README du 27 octobre 2026) : même encodeur et même décodeur, plus un routeur DASH (SR-SWA)
    // par couche. Au décodage, le code du dépôt coupe le tampon circulaire de 128 tokens (_ring_window) : KV complet sur
    // les 12 couches, et le routeur choisit 16 blocs de 32 tokens. Le pool de SGLang fourni dans le dépôt
    // (pool_configurator.py) garde en plus une clé de routeur de 128 valeurs au format du modèle (BF16) par couche et
    // par token, même en KV FP8. 128K par YaRN (facteur 4 sur 32K).
    id: "unlimited-ocr-2", label: "Unlimited-OCR 2 (MoE)", group: "Mixture of experts",
    params: 3.34, activeParams: 0.58, hidden: 1280, layers: 12, vocab: 129280, maxContext: 131072,
    parts: { vision: 0.401, experts: 2.422, odd: 0.816 }, routing: { experts: 64, active: 6, ffn: 896 },
    kv: [gqa(12, 10, 128), { layers: 12, width: 128, fixedBytes: 256 }],
    arch: "MoE 64 experts + 2 shared · 12 layers, sparse attention (GQA 10 KV × 128, 128-dim router keys) · DeepEncoder vision (SAM + CLIP)",
    repo: "baidu/Unlimited-OCR-2", released: "2026-10-27",
  },
  {
    // China Telecom (README, config.json, modeling_xing4_0.py, en-têtes safetensors) : 29 B annoncés, 31,2 B dans les
    // fichiers dont une couche MTP de 1,71 B ; 4 B actifs. Attention MLA de DeepSeek V3 sur les 40 couches (latent 512 +
    // 64 de RoPE : ce que vLLM et SGLang gardent en cache). 2 couches denses puis 38 MoE : 64 experts de 1 024 dont 4
    // actifs, plus un partagé. Hyper-connexions mHC (hc_mult 4) : le flux résiduel est 4 fois plus large, sans cache.
    // Contexte 262 144 (YaRN ×64 sur 4 096, déjà dans config.json). Xing4.0-29B-Intent (même labo, même architecture) a
    // les mêmes chiffres : sa fiche est celle-ci.
    id: "xing4.0-29b-a4b", label: "Xing4.0 29B-A4B (MoE)", group: "Mixture of experts",
    params: 31.22, activeParams: 4, hidden: 3584, layers: 40, vocab: 131072, maxContext: 262144,
    parts: { mtp: 1.71, experts: 26.776 }, routing: { experts: 64, active: 4, ffn: 1024 }, mtpBytes: 3419051776,
    kv: [mla(40, 512, 64)],
    arch: "MoE 64 experts + 1 shared · MLA (512 + 64 latent) on 40 layers · mHC hyper-connections",
    repo: "XingChen-AGI/Xing4.0-29B-A4B", released: "2026-09-16",
  },

  // Hybrides : attention linéaire (Gated DeltaNet) sur 3 couches sur 4.
  {
    id: "qwen3.5-4b", label: "Qwen3.5 4B", group: "Hybrid linear attention",
    params: 4.66, hidden: 2560, layers: 32, vocab: 248320, maxContext: 262144, tiedEmbeddings: true,
    parts: { mtp: 0.121, vision: 0.334 }, mtpBytes: 241199104,
    kv: [gqa(8, 4, 256)], stateBytesPerSeq: gdnState(24, 16, 32, 128, 128, 4),
    arch: "KV on 8 of 32 layers (GQA, 4 KV heads × 256) · 24 Gated DeltaNet layers",
    repo: "Qwen/Qwen3.5-4B", released: "2026-02-27",
  },
  {
    // OpenBMB (config.json, en-têtes safetensors) : décodeur de Qwen3.5 (qwen3_5_text) de 24 couches, 6 en attention complète
    // (2 têtes KV × 256) et 18 en Gated DeltaNet (16 têtes × 128), plus un encodeur de vision de 0,52 B. 1,55 B au total.
    id: "minicpm-v-4.7-1b", label: "MiniCPM-V-4.7 1B", group: "Hybrid linear attention",
    params: 1.55, hidden: 1024, layers: 24, vocab: 248094, maxContext: 262144,
    parts: { vision: 0.522 },
    kv: [gqa(6, 2, 256)], stateBytesPerSeq: gdnState(18, 16, 16, 128, 128, 4),
    arch: "KV on 6 of 24 layers (GQA, 2 KV heads × 256) · 18 Gated DeltaNet layers · vision encoder",
    repo: "openbmb/MiniCPM-V-4.7-1B", released: "2026-10-06",
  },
  {
    // KRAFTON (fiche officielle ; dépôt protégé : config.json illisible, champs relevés dans le code de la prise en charge
    // OpenVINO, huggingface/optimum-intel#2072). 25 couches : 18 Raon Delta Attention (40 têtes × 128, état fixe comme
    // Kimi Delta Attention : matrice 128 × 128 par tête et fenêtre de convolution de 3 tokens) et 7 MLA à porte sans RoPE,
    // dont le cache garde le latent de 512 et une clé k_pe de 64 : 7,9 Kio par token, les « about 8 KiB » de la fiche. 128
    // experts de 1 088 dont 6 actifs, plus un partagé ; checkpoint officiel en MXFP8 (experts), le reste en BF16 : 24 couches
    // × 128 × 3 × 2 560 × 1 088 = 25,67 B de paramètres FP8, le compte exact des fichiers.
    id: "raon-1-mini-preview", label: "Raon-1 mini preview (MoE)", group: "Hybrid linear attention",
    params: 28.78, activeParams: 4.3, hidden: 2560, layers: 25, vocab: 259840, maxContext: 262144,
    parts: { experts: 25.669 }, routing: { experts: 128, active: 6, ffn: 1088 },
    kv: [mla(7, 512, 64)], stateBytesPerSeq: kdaState(18, 40, 128, 4),
    native: { label: "MXFP8 experts (native)", needs: "fp8", bytes: 32_702_145_088 },
    arch: "MoE 128 experts + 1 shared · 7 gated MLA layers (512 + 64 latent, NoPE) + 18 Raon Delta Attention layers (40 heads × 128)",
    repo: "KRAFTON/Raon-1-mini-preview", released: "2026-10-21",
  },
  {
    id: "qwen3.5-9b", label: "Qwen3.5 9B", group: "Hybrid linear attention",
    params: 9.65, hidden: 4096, layers: 32, vocab: 248320, maxContext: 262144,
    parts: { mtp: 0.243, vision: 0.456 }, mtpBytes: 486581248,
    kv: [gqa(8, 4, 256)], stateBytesPerSeq: gdnState(24, 16, 32, 128, 128, 4),
    arch: "KV on 8 of 32 layers (GQA, 4 KV heads × 256) · 24 Gated DeltaNet layers",
    repo: "Qwen/Qwen3.5-9B", released: "2026-02-27",
  },
  {
    id: "qwen3.8-27b", label: "Qwen3.8 27B", group: "Hybrid linear attention",
    params: 27.78, hidden: 5120, layers: 64, vocab: 248320, maxContext: 262144,
    parts: { mtp: 0.425, vision: 0.461 }, mtpBytes: 849398784,
    kv: [gqa(16, 4, 256)], stateBytesPerSeq: gdnState(48, 16, 48, 128, 128, 4),
    arch: "KV on 16 of 64 layers (GQA, 4 KV heads × 256) · 48 Gated DeltaNet layers",
    repo: "Qwen/Qwen3.8-27B", released: "2026-08-05",
  },
  {
    id: "qwen3.6-35b-a3b", label: "Qwen3.6 35B-A3B (MoE)", group: "Hybrid linear attention",
    params: 35.95, activeParams: 3, hidden: 2048, layers: 40, vocab: 248320, maxContext: 262144,
    parts: { mtp: 0.845, vision: 0.447, experts: 32.212 }, routing: { experts: 256, active: 8 }, mtpBytes: 1689281536,
    kv: [gqa(10, 2, 256)], stateBytesPerSeq: gdnState(30, 16, 32, 128, 128, 4),
    arch: "MoE 256 experts · KV on 10 of 40 layers (GQA, 2 KV heads × 256) · 30 Gated DeltaNet layers",
    repo: "Qwen/Qwen3.6-35B-A3B", released: "2026-04-15",
  },
  {
    // DiffuSpace (README, config.json « clover_moe », en-têtes safetensors) : modèle de langage par diffusion construit sur
    // Qwen3.6-35B-A3B, dont il garde le modèle de langue (10 couches GQA 2 × 256, 30 Gated DeltaNet, 256 experts dont 8
    // actifs plus 1 partagé), sans vision ni MTP. Il produit le texte par blocs de 32 tokens ; chaque bloc résolu rejoint le
    // préfixe, mis en cache comme pour un modèle autorégressif. 128K. Moteurs : Transformers et les versions de SGLang et
    // llama.cpp de DiffuSpace. Apache 2.0, sorti le 28 octobre 2026.
    id: "clover-35b-a3b-preview", label: "Clover 35B-A3B Preview (MoE)", group: "Hybrid linear attention",
    params: 34.66, activeParams: 3, hidden: 2048, layers: 40, vocab: 248320, maxContext: 131072,
    parts: { experts: 32.212, odd: 0.002 }, routing: { experts: 256, active: 8 },
    kv: [gqa(10, 2, 256)], stateBytesPerSeq: gdnState(30, 16, 32, 128, 128, 4),
    arch: "Diffusion language model (32-token blocks) on the Qwen3.6-35B-A3B backbone · MoE 256 experts · KV on 10 of 40 layers (GQA, 2 KV heads × 256) · 30 Gated DeltaNet layers",
    repo: "Diffuspace/Clover-35B-A3B-Preview", released: "2026-10-28",
  },
  {
    id: "qwen3.5-122b-a10b", label: "Qwen3.5 122B-A10B (MoE)", group: "Hybrid linear attention",
    params: 125.09, activeParams: 10, hidden: 3072, layers: 48, vocab: 248320, maxContext: 262144,
    parts: { mtp: 2.524, vision: 0.451, experts: 115.964 }, routing: { experts: 256, active: 8 }, mtpBytes: 5047358464,
    kv: [gqa(12, 2, 256)], stateBytesPerSeq: gdnState(36, 16, 64, 128, 128, 4),
    arch: "MoE 256 experts · KV on 12 of 48 layers (GQA, 2 KV heads × 256) · 36 Gated DeltaNet layers",
    repo: "Qwen/Qwen3.5-122B-A10B", released: "2026-02-24",
  },
  {
    // Préversion de l'architecture Qwen4 (README) : 12 couches Qwen Sparse Attention (KV
    // complet, GQA 2 × 256, plus une clé d'indexeur de 128 par bloc de 4 tokens) et 36 Gated
    // DeltaNet. 180 B = 125 B (6 B actifs) + 51 B de tables n-gram + 4 B de MTP ; vLLM peut
    // garder les tables n-gram en RAM (VLLM_PLE_CPU_OFFLOAD=1) sur des GPU de 80 GB.
    id: "qwen3.8-flash-next", label: "Qwen3.8-Flash-Next (MoE)", group: "Hybrid linear attention",
    params: 180.0, activeParams: 6, hidden: 2560, layers: 48, vocab: 248320, maxContext: 262144,
    parts: { mtp: 2.607, vision: 0.449, experts: 120.796, lookup: 51.2, odd: 40.663 }, routing: { experts: 512, active: 10 }, mtpBytes: 5214301696,
    kv: [gqa(12, 2, 256), { layers: 12, width: 128, ratio: 4, fixedBytes: 256 }], stateBytesPerSeq: gdnState(36, 16, 48, 128, 128, 4),
    arch: "MoE 512 experts · 12 Qwen Sparse Attention layers (GQA, 2 KV × 256, + indexer keys per 4-token block) · 36 Gated DeltaNet layers · 51B n-gram embeddings",
    repo: "Qwen/Qwen3.8-Flash-Next", released: "2026-08-24",
  },
  {
    // Liquid AI (README, config.json, modeling_lfm3.py) : 24 couches, 13 convolutions courtes (état de 7 positions par canal),
    // 5 couches en fenêtre de 256 tokens et 6 globales, GQA 4 têtes KV × 128 ; 256K. Sorti le 27 octobre 2026 (LFM Open
    // License v1.0).
    id: "lfm3-1.7b", label: "LFM3 1.7B", group: "Hybrid linear attention",
    params: 1.73, hidden: 2048, layers: 24, vocab: 128000, maxContext: 262144, tiedEmbeddings: true,
    kv: [gqa(6, 4, 128), gqa(5, 4, 128, 256)], stateBytesPerSeq: convState(13, 2048, 7),
    arch: "6 global layers + 5 sliding-window layers (256 tokens), GQA 4 KV × 128 · 13 short-convolution layers",
    repo: "LiquidAI/LFM3-1.7B", released: "2026-10-27",
  },
  {
    // Le même modèle de langue et un encodeur de vision maison de 0,19 B (recensement) ; images et vidéo. 128K (config.json).
    id: "lfm3-vl-2b", label: "LFM3-VL 2B", group: "Hybrid linear attention",
    params: 1.92, hidden: 2048, layers: 24, vocab: 128000, maxContext: 128000, tiedEmbeddings: true,
    parts: { vision: 0.189 },
    kv: [gqa(6, 4, 128), gqa(5, 4, 128, 256)], stateBytesPerSeq: convState(13, 2048, 7),
    arch: "6 global layers + 5 sliding-window layers (256 tokens), GQA 4 KV × 128 · 13 short-convolution layers · vision encoder",
    repo: "LiquidAI/LFM3-VL-2B", released: "2026-10-27",
  },
  {
    // Modèle de décision construit sur LFM3-VL-2B (README) : une réponse calibrée en un seul passage, sans générer de tokens.
    id: "d1-2b", label: "d1 2B", group: "Hybrid linear attention",
    params: 1.92, hidden: 2048, layers: 24, vocab: 128000, maxContext: 128000, tiedEmbeddings: true,
    parts: { vision: 0.189 },
    kv: [gqa(6, 4, 128), gqa(5, 4, 128, 256)], stateBytesPerSeq: convState(13, 2048, 7),
    arch: "Decision model · 6 global layers + 5 sliding-window layers (256 tokens), GQA 4 KV × 128 · 13 short-convolution layers · vision encoder",
    repo: "LiquidAI/d1-2B", released: "2026-10-27",
  },
  {
    // Liquid AI (README) : 6 couches GQA (8 têtes KV × 64) et 18 convolutions courtes qui ne
    // gardent que 3 tokens par canal. 8,3 B, 1,5 B actifs ; deux couches denses en tête.
    id: "lfm2.5-8b-a1b", label: "LFM2.5 8B-A1B (MoE)", group: "Hybrid linear attention",
    params: 8.47, activeParams: 1.5, hidden: 2048, layers: 24, vocab: 128000, maxContext: 128000, tiedEmbeddings: true,
    parts: { experts: 7.751 }, routing: { experts: 32, active: 4 },
    kv: [gqa(6, 8, 64)], stateBytesPerSeq: convState(18, 2048, 3),
    arch: "MoE 32 experts · 6 attention layers (GQA, 8 KV × 64) + 18 short-convolution layers",
    repo: "LiquidAI/LFM2.5-8B-A1B", released: "2026-05-28",
  },
  {
    // Même architecture, plus grande : 10 couches GQA et 30 convolutions, 2,3 B actifs.
    id: "lfm2-24b-a2b", label: "LFM2 24B-A2B (MoE)", group: "Hybrid linear attention",
    params: 23.84, activeParams: 2.3, hidden: 2048, layers: 40, vocab: 65536, maxContext: 128000, tiedEmbeddings: true,
    parts: { experts: 22.951 }, routing: { experts: 64, active: 4 },
    kv: [gqa(10, 8, 64)], stateBytesPerSeq: convState(30, 2048, 3),
    arch: "MoE 64 experts · 10 attention layers (GQA, 8 KV × 64) + 30 short-convolution layers",
    repo: "LiquidAI/LFM2-24B-A2B", released: "2026-02-24",
  },

  // Fenêtre glissante. Gemma 4 : couches globales à géométrie distincte
  // (global_head_dim 512). Son K=V n'est pas exploité par le cache de vLLM ni de
  // Transformers, qui stockent quand même K et V : on compte donc les deux.
  {
    id: "gemma-4-12b", label: "Gemma 4 12B", group: "Sliding-window attention",
    params: 11.96, hidden: 3840, layers: 48, vocab: 262144, maxContext: 262144, tiedEmbeddings: true,
    parts: { vision: 0.052 },
    kv: [gqa(40, 8, 256, 1024), gqa(8, 1, 512)],
    arch: "40 sliding-window layers (1024 tokens, 8 KV × 256) · 8 global layers (1 KV × 512)",
    repo: "google/gemma-4-12B-it", released: "2026-05-23",
  },
  {
    id: "gemma-4-31b", label: "Gemma 4 31B", group: "Sliding-window attention",
    params: 31.27, hidden: 5376, layers: 60, vocab: 262144, maxContext: 262144, tiedEmbeddings: true,
    parts: { vision: 0.576 },
    kv: [gqa(50, 16, 256, 1024), gqa(10, 4, 512)],
    arch: "50 sliding-window layers (1024 tokens, 16 KV × 256) · 10 global layers (4 KV × 512)",
    repo: "google/gemma-4-31B-it", released: "2026-03-11",
  },
  {
    id: "gemma-4-26b-a4b", label: "Gemma 4 26B-A4B (MoE)", group: "Sliding-window attention",
    params: 25.81, activeParams: 4, hidden: 2816, layers: 30, vocab: 262144, maxContext: 262144, tiedEmbeddings: true,
    parts: { vision: 0.573, experts: 22.838, odd: 7.791 }, routing: { experts: 128, active: 8 },
    kv: [gqa(25, 8, 256, 1024), gqa(5, 2, 512)],
    arch: "MoE 128 experts · 25 sliding-window layers (1024 tokens) · 5 global layers (2 KV × 512)",
    repo: "google/gemma-4-26B-A4B-it", released: "2026-03-11",
  },
  {
    id: "gpt-oss-20b", label: "gpt-oss 20B (MoE)", group: "Sliding-window attention",
    params: 20.91, activeParams: 3.6, hidden: 2880, layers: 24, vocab: 201088, maxContext: 131072,
    parts: { experts: 19.117, odd: 19.473 }, routing: { experts: 32, active: 4, ffn: 2880 }, ggufExpertBits: 4.25,
    kv: [gqa(12, 8, 64, 128), gqa(12, 8, 64)],
    native: { label: "MXFP4 (native)", needs: "fp4", bytes: 13_761_316_904 },
    arch: "MoE 32 experts · alternating 128-token sliding window / full attention (8 KV × 64)",
    repo: "openai/gpt-oss-20b", released: "2025-08-04",
  },
  {
    id: "gpt-oss-120b", label: "gpt-oss 120B (MoE)", group: "Sliding-window attention",
    params: 116.83, activeParams: 5.1, hidden: 2880, layers: 36, vocab: 201088, maxContext: 131072,
    parts: { experts: 114.702, odd: 115.246 }, routing: { experts: 128, active: 4, ffn: 2880 }, ggufExpertBits: 4.25,
    kv: [gqa(18, 8, 64, 128), gqa(18, 8, 64)],
    native: { label: "MXFP4 (native)", needs: "fp4", bytes: 65_248_893_184 },
    arch: "MoE 128 experts · alternating 128-token sliding window / full attention (8 KV × 64)",
    repo: "openai/gpt-oss-120b", released: "2025-08-04",
  },

  // Llama 4 : attention par blocs de 8 192 tokens sur 3 couches sur 4 (le cache n'en
  // garde qu'un bloc), attention globale sans RoPE sur la 4e. Config lue sur le miroir
  // unsloth (identique au dépôt meta-llama, protégé).
  {
    id: "llama-4-scout", label: "Llama 4 Scout 17B-16E (MoE)", group: "Sliding-window attention",
    params: 108.64, activeParams: 17, hidden: 5120, layers: 48, vocab: 202048, maxContext: 10485760,
    parts: { vision: 0.872, experts: 96.637 }, routing: { experts: 16, active: 1 },
    kv: [gqa(36, 8, 128, 8192), gqa(12, 8, 128)],
    arch: "MoE 16 experts · 36 chunked-attention layers (8192-token chunks, 8 KV × 128) · 12 global layers",
    repo: "meta-llama/Llama-4-Scout-17B-16E-Instruct", released: "2025-04-02",
  },
  {
    id: "llama-4-maverick", label: "Llama 4 Maverick 17B-128E (MoE)", group: "Sliding-window attention",
    params: 401.58, activeParams: 17, hidden: 5120, layers: 48, vocab: 202048, maxContext: 1048576,
    parts: { vision: 0.872, experts: 386.547 }, routing: { experts: 128, active: 1 },
    kv: [gqa(36, 8, 128, 8192), gqa(12, 8, 128)],
    arch: "MoE 128 experts · 36 chunked-attention layers (8192-token chunks, 8 KV × 128) · 12 global layers",
    repo: "meta-llama/Llama-4-Maverick-17B-128E-Instruct", released: "2025-04-01",
  },
  {
    // Aleph Alpha (README) : une couche d'attention complète pour quatre en fenêtre glissante
    // (512 tokens précédents + le token courant), GQA 4 × 128 partout. 3,46 B actifs ; contexte
    // natif 256K (1M par extrapolation). Servi et évalué avec un cache KV FP8.
    id: "kolibri-1", label: "Kolibri-1 (MoE)", group: "Sliding-window attention",
    params: 78.1, activeParams: 3.46, hidden: 2560, layers: 50, vocab: 128000, maxContext: 262144,
    parts: { experts: 75.497 }, routing: { experts: 384, active: 6 },
    kv: [gqa(10, 4, 128), gqa(40, 4, 128, 513)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 78_841_467_208 },
    fp8Kv: true,
    arch: "MoE 384 experts · 10 full-attention layers + 40 sliding-window layers (513 tokens), GQA 4 KV × 128",
    repo: "Aleph-Alpha/Kolibri-1", released: "2026-10-02",
  },
  {
    // Recursive (README, config.json, en-têtes safetensors) : 39,8 B, environ 2,5 B actifs avec la tête de sortie (fiche).
    // 49 couches : 2 denses puis 47 MoE (256 experts de 512 dont 6 actifs, plus un partagé) ; attention complète sur 14
    // couches, fenêtre de 513 tokens (le token courant et les 512 précédents) sur 35, GQA 4 têtes KV × 128. Sortie le 21 octobre 2026.
    id: "flint", label: "Recursive Flint (MoE)", group: "Sliding-window attention",
    params: 39.79, activeParams: 2.5, hidden: 2048, layers: 49, vocab: 150144, maxContext: 262144,
    parts: { experts: 37.849 }, routing: { experts: 256, active: 6, ffn: 512 },
    kv: [gqa(14, 4, 128), gqa(35, 4, 128, 513)],
    arch: "MoE 256 experts + 1 shared · 14 full-attention layers + 35 sliding-window layers (513 tokens), GQA 4 KV × 128",
    repo: "RecursiveSI/flint", released: "2026-10-21",
  },
  {
    // NVIDIA Nemotron Labs (README, config.json, vLLM #60149) : 4 B, transformeur « en boucle ». 72 sous-couches (attention et
    // MLP en alternance) : 16 d'entrée, 40 récurrentes rejouées n_recurrent_loops fois (2, le réglage recommandé), 16 de
    // sortie. vLLM garde un cache par boucle (recurrent_kv_cache_mode « per_loop », la commande du README) : 2 + 5 × 2 + 2 = 14
    // couches complètes et 6 + 15 × 2 + 6 = 42 en fenêtre de 1 024 tokens, 4 têtes KV × 256 chacune. Le mode « last_loop »
    // (un seul cache par couche récurrente) en garde 9 et 27.
    id: "nemotron-labs-dory-1.0-4b", label: "Nemotron Labs Dory 1.0 4B", group: "Sliding-window attention",
    params: 3.98, hidden: 2560, layers: 72, vocab: 131072, maxContext: 262144,
    kv: [gqa(14, 4, 256), gqa(42, 4, 256, 1024)],
    arch: "Looped transformer (16 input + 40 recurrent × 2 loops + 16 output sub-layers) · 14 full-attention + 42 sliding-window caches (1024 tokens), GQA 4 KV × 256",
    repo: "nvidia/NVIDIA-Nemotron-Labs-Dory1.0-4B-BF16", released: "2026-10-16",
  },
  {
    // StepFun (README, config.json) : 196 B + tête de 0,81 B, 11 B actifs ; 3 couches denses puis 42 MoE
    // (288 experts de 1 280 + 1 partagé, 8 actifs). Une couche d'attention complète pour trois en fenêtre
    // de 512 tokens : 64 têtes Q sur les complètes, 96 sur les glissantes, 8 têtes KV × 128 partout.
    // 256K tokens (rope llama3 ×2 sur 131 072, dans config.json). 3 couches MTP (MTP-3, 2,43 B) comptées
    // dans le checkpoint, chargées seulement en décodage spéculatif. Recensement : experts empilés par
    // projection (« moe.gate_proj »…), voir scripts/sources/safetensors.ts.
    id: "step-3.5-flash", label: "Step 3.5 Flash (MoE)", group: "Sliding-window attention",
    params: 199.38, activeParams: 11, hidden: 4096, layers: 45, vocab: 128896, maxContext: 262144,
    parts: { mtp: 2.428, experts: 190.254 }, routing: { experts: 288, active: 8, ffn: 1280 }, mtpBytes: 4856342016,
    kv: [gqa(12, 8, 128), gqa(33, 8, 128, 512)],
    arch: "MoE 288 experts + 1 shared · 12 full-attention layers + 33 sliding-window layers (512 tokens), GQA 8 KV × 128",
    repo: "stepfun-ai/Step-3.5-Flash", released: "2026-02-01",
  },
  {
    // StepFun (README) : le décodeur de Step 3.5 Flash (config.json identique : 12 couches complètes, 33 en fenêtre de 512,
    // 8 têtes KV × 128 ; 3 couches MTP) plus un encodeur de vision de 1,96 B (recensement). 256K.
    id: "step-3.7-flash", label: "Step 3.7 Flash (MoE)", group: "Sliding-window attention",
    params: 201.37, activeParams: 11, hidden: 4096, layers: 45, vocab: 128896, maxContext: 262144,
    parts: { mtp: 2.428, vision: 1.956, experts: 190.254 }, routing: { experts: 288, active: 8, ffn: 1280 }, mtpBytes: 4856342016,
    kv: [gqa(12, 8, 128), gqa(33, 8, 128, 512)],
    arch: "MoE 288 experts + 1 shared · 12 full-attention layers + 33 sliding-window layers (512 tokens), GQA 8 KV × 128 · vision encoder",
    repo: "stepfun-ai/Step-3.7-Flash", released: "2026-05-23",
  },
  {
    // poolside (San Francisco ; README, config.json, vllm/model_executor/models/laguna.py) : 12 couches globales et 36 en
    // fenêtre de 512 tokens, 8 têtes KV × 128 partout (le nombre de têtes Q change, pas les KV). 1M par YaRN (facteur 128
    // sur 8 192, couches globales). 118 B dont 8 B actifs (README). OpenMDW-1.1. Sorti le 21 juillet 2026.
    id: "laguna-s-2.1", label: "Laguna S 2.1 (MoE)", group: "Sliding-window attention",
    params: 117.56, activeParams: 8, hidden: 3072, layers: 48, vocab: 100352, maxContext: 1048576,
    parts: { experts: 113.548 }, routing: { experts: 256, active: 10, ffn: 1024 },
    kv: [gqa(12, 8, 128), gqa(36, 8, 128, 512)],
    arch: "MoE 256 experts + 1 shared · 12 global layers + 36 sliding-window layers (512 tokens), GQA 8 KV × 128",
    repo: "poolside/Laguna-S-2.1", released: "2026-07-21",
  },
  {
    // Même architecture en plus petit (README : 10 couches globales, 30 en fenêtre de 512, 33 B dont 3 B actifs ; 256K).
    id: "laguna-xs-2.1", label: "Laguna XS 2.1 (MoE)", group: "Sliding-window attention",
    params: 33.44, activeParams: 3, hidden: 2048, layers: 40, vocab: 100352, maxContext: 262144,
    parts: { experts: 31.407 }, routing: { experts: 256, active: 8, ffn: 512 },
    kv: [gqa(10, 8, 128), gqa(30, 8, 128, 512)],
    arch: "MoE 256 experts + 1 shared · 10 global layers + 30 sliding-window layers (512 tokens), GQA 8 KV × 128",
    repo: "poolside/Laguna-XS-2.1", released: "2026-06-20",
  },
  {
    // Cohere (README, config.json → text_config « cohere2_moe ») : 8 couches globales (sans position) et 24 en fenêtre de
    // 4 096 tokens, 8 têtes KV × 128 ; 128 experts dont 8 actifs. 218 B dont 25 B actifs. 128K d'entrée et 64K de sortie
    // (README), 200 000 dans config.json. Versions FP8 et W4A4 (NVFP4) publiées à part. Apache 2.0.
    id: "command-a-plus", label: "Command A+ (MoE)", group: "Sliding-window attention",
    params: 218.75, activeParams: 25, hidden: 4096, layers: 32, vocab: 262144, maxContext: 200000, tiedEmbeddings: true,
    parts: { vision: 0.495, experts: 206.158 }, routing: { experts: 128, active: 8, ffn: 4096 },
    kv: [gqa(8, 8, 128), gqa(24, 8, 128, 4096)],
    arch: "MoE 128 experts + shared · 8 global layers (no position embedding) + 24 sliding-window layers (4096 tokens), GQA 8 KV × 128 · vision encoder",
    repo: "CohereLabs/command-a-plus-05-2026-bf16", released: "2026-05-11",
  },
  {
    // Thinking Machines Lab (README, config.json, vllm/models/inkling) : 35 couches locales (fenêtre de 512 tokens, 8 têtes
    // KV × 128) et 7 globales (8 têtes KV × 128). Convolutions courtes à état (K, V, sorties d'attention et de MLP,
    // 4 positions) : vLLM les garde comme une fenêtre de 4 tokens, 32 Kio par couche et par position (sconv_swa_attn.py).
    // 266 B aux en-têtes safetensors (276 au README), 12 B actifs, 1M. NVFP4 publié à part (experts seulement).
    id: "inkling-small", label: "Inkling Small (MoE)", group: "Sliding-window attention",
    params: 265.96, activeParams: 12, hidden: 4096, layers: 42, vocab: 201024, maxContext: 1048576,
    parts: { mtp: 2.232, vision: 0.069, experts: 257.698 }, routing: { experts: 256, active: 6, ffn: 2048 }, mtpBytes: 4463824912,
    kv: [gqa(7, 8, 128), gqa(35, 8, 128, 512)], stateBytesPerSeq: 42 * 4 * 32768,
    native: { label: "NVFP4 experts + BF16 (native)", needs: "fp8", bytes: 170_733_233_632, repo: "thinkingmachines/Inkling-Small-NVFP4" },
    arch: "MoE 256 experts + 2 shared · 7 global layers + 35 sliding-window layers (512 tokens), GQA 8 KV × 128 · short convolutions · image and audio input",
    repo: "thinkingmachines/Inkling-Small", released: "2026-07-27",
  },
  {
    // Meta (README, config.json) : 29,6 B dont un encodeur de vision de 1,88 B (recensement), dense,
    // distillé de Muse Spark. 52 couches : une en attention complète pour trois en fenêtre de 2 048 tokens,
    // GQA 2 têtes KV × 128. Contexte 131 072. Licence Apache 2.0.
    id: "muse-glimmer-30b", label: "Muse Glimmer 30B", group: "Sliding-window attention",
    params: 29.78, hidden: 6656, layers: 52, vocab: 202048, maxContext: 131072,
    parts: { vision: 1.88 },
    kv: [gqa(13, 2, 128), gqa(39, 2, 128, 2048)],
    arch: "52 layers · 13 full-attention layers + 39 sliding-window layers (2048 tokens), GQA 2 KV × 128",
    repo: "meta-models/Muse-Glimmer-30B", released: "2026-08-09",
  },

  // Hybrides Mamba-2 : très peu de couches d'attention.
  {
    id: "nemotron-3-nano", label: "Nemotron 3 Nano 30B-A3B", group: "Hybrid Mamba",
    params: 31.58, activeParams: 3, hidden: 2688, layers: 52, vocab: 131072, maxContext: 262144,
    parts: { experts: 29.375, odd: 30.906 }, routing: { experts: 128, active: 6 },
    kv: [gqa(6, 2, 128)], stateBytesPerSeq: mamba2State(23, 64, 64, 128, 8, 4),
    arch: "KV on 6 of 52 layers (GQA, 2 KV × 128) · 23 Mamba-2 layers · 23 MoE layers",
    repo: "nvidia/NVIDIA-Nemotron-3-Nano-30B-A3B-BF16", released: "2025-12-04",
  },
  {
    id: "nemotron-3-super", label: "Nemotron 3 Super 120B-A12B", group: "Hybrid Mamba",
    params: 123.61, activeParams: 12, hidden: 4096, layers: 88, vocab: 131072, maxContext: 262144,
    parts: { mtp: 2.942, experts: 112.743, odd: 56.373 }, routing: { experts: 512, active: 22 }, mtpBytes: 5884651520,
    kv: [gqa(8, 2, 128)], stateBytesPerSeq: mamba2State(40, 128, 64, 128, 8, 4),
    arch: "KV on 8 of 88 layers (GQA, 2 KV × 128) · 40 Mamba-2 layers · 40 MoE layers",
    repo: "nvidia/NVIDIA-Nemotron-3-Super-120B-A12B-BF16", released: "2026-03-10",
  },
  {
    // NVIDIA (README, config.json → layers_block_type, en-têtes safetensors) : 108 couches, dont 12 d'attention (2 têtes KV ×
    // 128), 48 Mamba-2 (256 têtes × 64, état de 128, 8 groupes) et 48 LatentMoE (512 experts, 22 actifs). 550 B dont 55 B
    // actifs (README) ; couche MTP de 11,2 B. 256K par défaut dans vLLM et SGLang, 1M avec une variable (comme Nemotron 3
    // Super). Checkpoint NVFP4 publié à part (Blackwell). Sorti le 4 juin 2026.
    id: "nemotron-3-ultra", label: "Nemotron 3 Ultra 550B-A55B", group: "Hybrid Mamba",
    params: 560.52, activeParams: 55, hidden: 8192, layers: 108, vocab: 131072, maxContext: 262144,
    parts: { mtp: 11.216, experts: 515.396, odd: 0.004 }, routing: { experts: 512, active: 22, ffn: 5120 }, mtpBytes: 22431221760,
    kv: [gqa(12, 2, 128)], stateBytesPerSeq: mamba2State(48, 256, 64, 128, 8, 4),
    native: { label: "NVFP4 (native)", needs: "fp4", bytes: 352_308_689_576, repo: "nvidia/NVIDIA-Nemotron-3-Ultra-550B-A55B-NVFP4" },
    arch: "KV on 12 of 108 layers (GQA, 2 KV × 128) · 48 Mamba-2 layers · 48 LatentMoE layers",
    repo: "nvidia/NVIDIA-Nemotron-3-Ultra-550B-A55B-BF16", released: "2026-06-04",
  },
  {
    // Mistral AI (config.json « mamba2 ») : 64 couches Mamba-2 (128 têtes × 64, état de 128, 8 groupes, convolution de 4), aucune
    // couche d'attention, donc pas de cache KV : un état fixe par requête. Testé jusqu'à 256K tokens (annonce de Mistral,
    // mistral.ai/news/codestral-mamba). Apache 2.0, sorti le 16 juillet 2024.
    id: "mamba-codestral-7b", label: "Mamba-Codestral 7B", group: "Hybrid Mamba",
    params: 7.29, hidden: 4096, layers: 64, vocab: 32768, maxContext: 262144,
    kv: [], stateBytesPerSeq: mamba2State(64, 128, 64, 128, 8, 4),
    arch: "64 Mamba-2 layers, no attention: fixed state instead of a KV cache",
    repo: "mistralai/Mamba-Codestral-7B-v0.1", released: "2024-07-16",
  },
  {
    // NVIDIA (README, config.json → llm_config, en-têtes safetensors) : même modèle de langue que Nemotron 3 Super (88 couches :
    // 8 d'attention à 2 têtes KV × 128, 40 Mamba-2, 40 MoE de 512 experts dont 22 actifs, une couche MTP), contexte porté à 1M,
    // plus l'encodeur de vision C-RADIO (0,65 B). NVIDIA publie aussi le NVFP4 (75 Go), servi depuis son dépôt. Sortie : 15 octobre 2026.
    id: "nemotron-3.5-super-vl-120b-a12b", label: "Nemotron 3.5 Super VL 120B-A12B", group: "Hybrid Mamba",
    params: 124.45, activeParams: 12, hidden: 4096, layers: 88, vocab: 131072, maxContext: 1048576,
    parts: { mtp: 2.942, vision: 0.654, experts: 112.743, odd: 56.373 }, routing: { experts: 512, active: 22 }, mtpBytes: 5884651520,
    kv: [gqa(8, 2, 128)], stateBytesPerSeq: mamba2State(40, 128, 64, 128, 8, 4),
    native: { label: "NVFP4 (native)", needs: "fp4", bytes: 75_010_188_240, repo: "nvidia/NVIDIA-Nemotron-3.5-Super-VL-120B-A12B-NVFP4" },
    arch: "KV on 8 of 88 layers (GQA, 2 KV × 128) · 40 Mamba-2 layers · 40 MoE layers · C-RADIO vision encoder",
    repo: "nvidia/NVIDIA-Nemotron-3.5-Super-VL-120B-A12B-BF16", released: "2026-10-15",
  },
  {
    // NVIDIA (README, config.json, en-têtes safetensors) : Super VL compressé (Puzzle), mêmes 88 couches (8 d'attention, 40 Mamba-2,
    // 40 MoE) mais de 240 à 496 experts par couche, 9 actifs : 2,6 % des experts lus par token, soit 9 sur 351 en moyenne pondérée.
    // Checkpoint officiel « 24GB » : experts en IQ2 (2 bits, entraînement adapté), couches Mamba en NVFP4, embeddings en Q8_0 ;
    // 24,08 Go, vLLM sur Ampere, Hopper et Blackwell seulement (noyaux CUDA : needs « cuda »). Sortie : 15 octobre 2026.
    id: "nemotron-3.5-bolt-74b-a9b", label: "Nemotron 3.5 Bolt 74B-A9B", group: "Hybrid Mamba",
    params: 74.89, activeParams: 9, hidden: 4096, layers: 88, vocab: 131072, maxContext: 262144,
    parts: { vision: 0.654, experts: 66.144 }, routing: { experts: 351, active: 9 },
    kv: [gqa(8, 2, 128)], stateBytesPerSeq: mamba2State(40, 128, 64, 128, 8, 4),
    native: { label: "2-bit experts, 24GB (native)", needs: "cuda", bytes: 24_083_745_496, repo: "nvidia/NVIDIA-Nemotron-3.5-Bolt-74B-A9B-24GB" },
    arch: "KV on 8 of 88 layers (GQA, 2 KV × 128) · 40 Mamba-2 layers · 40 MoE layers (240 to 496 experts) · C-RADIO vision encoder",
    repo: "nvidia/NVIDIA-Nemotron-3.5-Bolt-74B-A9B-BF16", released: "2026-10-15",
  },

  // Grands MoE.
  {
    // MiniMax (README, config.json, en-têtes safetensors, vLLM vllm/models/minimax_m3) : environ 428 B, 23 B actifs, contexte 1M.
    // 60 couches, MiniMax Sparse Attention partout : le KV complet (GQA 4 têtes × 128), plus une clé d'index de 128 valeurs par
    // token et par couche, gardée en BF16 par vLLM (MiniMaxM3IndexerCache). 3 couches denses puis 57 MoE (128 experts de 3 072,
    // 4 actifs, plus un partagé) ; encodeur de vision de 0,68 B. Seul checkpoint publié : experts en NVFP4, le reste en MXFP8
    // (250,1 Go, sans le brouillon DSpark). Public le 22 octobre 2026 (dépôt créé le 17).
    id: "minimax-m3.1-flash", label: "MiniMax M3.1 Flash (MoE)", group: "Large MoE",
    params: 427.05, activeParams: 23, hidden: 6144, layers: 60, vocab: 200064, maxContext: 1048576,
    parts: { vision: 0.677, experts: 413.122 }, routing: { experts: 128, active: 4, ffn: 3072 },
    kv: [gqa(60, 4, 128), { layers: 60, width: 128, fixedBytes: 256 }],
    native: { label: "NVFP4 experts + MXFP8 (native)", needs: "fp4", bytes: 250_116_419_880 },
    arch: "MoE 128 experts + 1 shared · 60 layers, MiniMax Sparse Attention (GQA 4 KV × 128, 128-dim index keys) · vision encoder",
    repo: "MiniMaxAI/MiniMax-M3.1-Flash", released: "2026-10-22",
  },
  {
    // Même modèle de base (juin 2026) : attention clairsemée sur 57 des 60 couches seulement (sparse_attention_freq), donc 57
    // couches de clés d'index. BF16 (854 Go) ; MiniMax publie aussi le MXFP8 (443,7 Go), servi depuis son dépôt.
    id: "minimax-m3", label: "MiniMax M3 (MoE)", group: "Large MoE",
    params: 427.04, activeParams: 23, hidden: 6144, layers: 60, vocab: 200064, maxContext: 1048576,
    parts: { vision: 0.677, experts: 413.122 }, routing: { experts: 128, active: 4, ffn: 3072 },
    kv: [gqa(60, 4, 128), { layers: 57, width: 128, fixedBytes: 256 }],
    native: { label: "MXFP8 (native)", needs: "fp8", bytes: 443_749_077_256, repo: "MiniMaxAI/MiniMax-M3-MXFP8" },
    arch: "MoE 128 experts + 1 shared · 60 layers, MiniMax Sparse Attention on 57 (GQA 4 KV × 128, 128-dim index keys) · vision encoder",
    repo: "MiniMaxAI/MiniMax-M3", released: "2026-06-02",
  },
  {
    id: "minimax-m2.7", label: "MiniMax M2.7 (MoE)", group: "Large MoE",
    params: 228.69, activeParams: 10, hidden: 3072, layers: 62, vocab: 200064, maxContext: 204800,
    parts: { experts: 224.68 }, routing: { experts: 256, active: 8 },
    kv: [gqa(62, 8, 128)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 230_134_260_592 },
    arch: "MoE 256 experts · 62 layers, full attention (GQA, 8 KV × 128)",
    repo: "MiniMaxAI/MiniMax-M2.7", released: "2026-04-09",
  },
  {
    id: "deepseek-r1", label: "DeepSeek R1 (MoE)", group: "Large MoE",
    params: 684.49, activeParams: 37, hidden: 7168, layers: 61, vocab: 129280, maxContext: 163840,
    parts: { mtp: 13.463, experts: 653.909 }, routing: { experts: 256, active: 8 }, mtpBytes: 15424227552,
    kv: [mla(61, 512, 64)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 688_586_727_753 },
    arch: "MoE 256 experts · MLA (512 + 64 latent) on 61 layers",
    repo: "deepseek-ai/DeepSeek-R1", released: "2025-01-20",
  },
  {
    id: "kimi-k2.6", label: "Kimi K2.6 (MoE)", group: "Large MoE",
    params: 1026.88, activeParams: 32, hidden: 7168, layers: 61, vocab: 163840, maxContext: 262144,
    parts: { vision: 0.471, experts: 1014.686 }, routing: { experts: 384, active: 8 },
    kv: [mla(61, 512, 64)],
    native: { label: "INT4 (native)", bytes: 595_177_988_208 },
    arch: "MoE 384 experts · MLA (512 + 64 latent) on 61 layers · INT4 checkpoint (group size 32)",
    repo: "moonshotai/Kimi-K2.6", released: "2026-04-14",
  },
  {
    // DSA : seules 21 couches gardent leurs propres clés d'indexeur, 57 les partagent.
    // Paramètres actifs : la fiche ne les donne pas ; calculés depuis le recensement des tenseurs
    // et le config.json (8 experts routés sur 256), comme activeFor : 40,3 B.
    id: "glm-5.3", label: "GLM-5.3 (MoE)", group: "Large MoE",
    params: 753.33, activeParams: 40.3, hidden: 6144, layers: 78, vocab: 154880, maxContext: 1048576,
    parts: { mtp: 9.953, experts: 724.776 }, routing: { experts: 256, active: 8 }, mtpBytes: 10032632960,
    kv: [{ ...mla(78, 512, 64), fp8Bytes: SPARSE_MLA_FP8_BYTES }, { layers: 21, width: 128, fixedBytes: DSA_INDEXER_BYTES(128) }],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 755_632_050_320 },
    arch: "MoE 256 experts · MLA (512 + 64 latent) on 78 layers · sparse-attention indexer keys on 21",
    repo: "zai-org/GLM-5.3", released: "2026-08-25",
  },
  {
    // Shanghai AI Lab, sur la base GLM-5.2 (README) : config.json et recensement des tenseurs identiques
    // à GLM-5.3, même calcul des paramètres actifs. Le config.json garde le 1M de GLM ; le README donne
    // 512K (256K pour la Preview) comme limite réelle. Checkpoint FP8 publié à part.
    id: "atria-dawn", label: "Atria Dawn (MoE)", group: "Large MoE",
    params: 753.33, activeParams: 40.3, hidden: 6144, layers: 78, vocab: 154880, maxContext: 524288,
    parts: { mtp: 9.953, experts: 724.776 }, routing: { experts: 256, active: 8 }, mtpBytes: 10032632960,
    kv: [{ ...mla(78, 512, 64), fp8Bytes: SPARSE_MLA_FP8_BYTES }, { layers: 21, width: 128, fixedBytes: DSA_INDEXER_BYTES(128) }],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 755_632_020_776, repo: "internlm/Atria-Dawn-FP8" },
    arch: "MoE 256 experts · MLA (512 + 64 latent) on 78 layers · sparse-attention indexer keys on 21",
    repo: "internlm/Atria-Dawn", released: "2026-10-23",
  },
  {
    id: "atria-dawn-preview", label: "Atria Dawn Preview (MoE)", group: "Large MoE",
    params: 753.33, activeParams: 40.3, hidden: 6144, layers: 78, vocab: 154880, maxContext: 262144,
    parts: { mtp: 9.953, experts: 724.776 }, routing: { experts: 256, active: 8 }, mtpBytes: 10032632960,
    kv: [{ ...mla(78, 512, 64), fp8Bytes: SPARSE_MLA_FP8_BYTES }, { layers: 21, width: 128, fixedBytes: DSA_INDEXER_BYTES(128) }],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 755_632_020_496, repo: "internlm/Atria-Dawn-Preview-FP8" },
    arch: "MoE 256 experts · MLA (512 + 64 latent) on 78 layers · sparse-attention indexer keys on 21",
    repo: "internlm/Atria-Dawn-Preview", released: "2026-09-11",
  },
  {
    // Tencent (README, config.json « hy_v4 ») : DSA à porte comme GLM-5 (recette vLLM : FLASHMLA_SPARSE), MLA (512 + 64) sur
    // les 78 couches, clés d'indexeur propres sur 21 et partagées sur 57 (IndexCache). 770 B dont 49 B actifs, plus une
    // couche MTP de 10 B ; 1M. FP8 publié à part. Apache 2.0, sorti le 27 août 2026.
    id: "hy4-preview", label: "Hy4 Preview (MoE)", group: "Large MoE",
    params: 779.96, activeParams: 49, hidden: 6144, layers: 78, vocab: 120832, maxContext: 1048576,
    parts: { mtp: 10.054, experts: 744.103 }, routing: { experts: 256, active: 8, ffn: 2048 }, mtpBytes: 10540161152,
    kv: [{ ...mla(78, 512, 64), fp8Bytes: SPARSE_MLA_FP8_BYTES }, { layers: 21, width: 128, fixedBytes: DSA_INDEXER_BYTES(128) }],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 813_766_152_348, repo: "tencent/Hy4-preview-FP8" },
    arch: "MoE 256 experts + 1 shared · gated DSA: MLA (512 + 64 latent) on 78 layers · sparse-attention indexer keys on 21",
    repo: "tencent/Hy4-preview", released: "2026-08-27",
  },
  {
    // LG AI Research (README, config.json, vllm/model_executor/models/exaone_moe.py) : fenêtre propre à chaque couche
    // (sliding_windows) : 57 couches à 128 tokens, une à 4 096, 20 globales ; 8 têtes KV × 128. 750 B dont 37 B actifs,
    // 256K. Apache 2.0.
    id: "k-exaone-2.0", label: "K-EXAONE 2.0 750B-A37B (MoE)", group: "Large MoE",
    params: 749.36, activeParams: 37, hidden: 6144, layers: 78, vocab: 153600, maxContext: 262144,
    parts: { mtp: 0.529, experts: 734.439 }, routing: { experts: 256, active: 8, ffn: 2048 },
    kv: [gqa(20, 8, 128), gqa(57, 8, 128, 128), gqa(1, 8, 128, 4096)],
    arch: "MoE 256 experts + 1 shared · 20 global layers + 58 sliding-window layers (57 × 128 tokens, 1 × 4096), GQA 8 KV × 128",
    repo: "LGAI-EXAONE/K-EXAONE-2.0-750B-A37B", released: "2026-07-29",
  },
  {
    // Thinking Machines Lab (README, config.json, vllm/models/inkling) : 55 couches locales (fenêtre de 512 tokens, 16 têtes
    // KV × 128) et 11 globales (8 têtes KV × 128) ; convolutions courtes à état gardées par vLLM comme une fenêtre de 4
    // tokens, 32 Kio par couche et par position (sconv_swa_attn.py). 952 B aux en-têtes safetensors (975 au README), 41 B
    // actifs, 1M. NVFP4 publié à part (experts seulement) ; recette vLLM : sur Hopper, experts décompressés en BF16 à la
    // volée. Apache 2.0, sorti le 15 juillet 2026.
    id: "inkling", label: "Inkling (MoE)", group: "Large MoE",
    params: 952.38, activeParams: 41, hidden: 6144, layers: 66, vocab: 201024, maxContext: 1048576,
    parts: { mtp: 5.26, vision: 0.092, experts: 927.713 }, routing: { experts: 256, active: 6, ffn: 3072 }, mtpBytes: 10520891408,
    kv: [gqa(11, 8, 128), gqa(55, 16, 128, 512)], stateBytesPerSeq: 66 * 4 * 32768,
    native: { label: "NVFP4 experts + BF16 (native)", needs: "fp8", bytes: 592_005_609_232, repo: "thinkingmachines/Inkling-NVFP4" },
    arch: "MoE 256 experts + 2 shared · 11 global layers (8 KV × 128) + 55 sliding-window layers (512 tokens, 16 KV × 128) · short convolutions · image and audio input",
    repo: "thinkingmachines/Inkling", released: "2026-07-15",
  },
  {
    // README + recipe vLLM : 11 couches d'attention clairsemée (MLA sans RoPE, latent de 512,
    // clés d'indexeur de 128 regroupées par blocs de 4 tokens) et 34 couches Kimi Delta
    // Attention à état fixe. 320 B annoncés (321,3 avec la vision), 18 B actifs.
    id: "glm-5.3-flash", label: "GLM-5.3-Flash (MoE)", group: "Large MoE",
    params: 321.32, activeParams: 18, hidden: 4096, layers: 45, vocab: 154880, maxContext: 1048576,
    parts: { mtp: 7.433, vision: 0.564, experts: 304.406 }, routing: { experts: 288, active: 8 }, mtpBytes: 7493399168,
    kv: [{ ...mla(11, 512, 0), fp8Bytes: SPARSE_MLA_FP8_BYTES }, { layers: 11, width: 128, ratio: 4, fixedBytes: DSA_INDEXER_BYTES(128) }],
    stateBytesPerSeq: kdaState(34, 64, 128, 4),
    native: { label: "FP8 (native)", needs: "fp8", bytes: 328_337_455_672 },
    arch: "MoE 288 experts · sparse MLA (512 latent, no RoPE) on 11 of 45 layers, indexer keys pooled by 4 · 34 Kimi Delta Attention layers",
    repo: "zai-org/GLM-5.3-Flash", released: "2026-08-25",
  },
  {
    // inference/model.py : chaque couche garde une fenêtre de 128 tokens + une
    // version compressée du reste (4× ou 128×) ; indexeur sur les couches ratio 4.
    id: "deepseek-v4-flash", label: "DeepSeek V4 Flash (MoE)", group: "Large MoE",
    params: 290.94, activeParams: 13, hidden: 4096, layers: 43, vocab: 129280, maxContext: 1048576,
    parts: { mtp: 6.61, experts: 277.025 }, routing: { experts: 256, active: 6, ffn: 2048 }, mtpBytes: 3593787756,
    kv: [
      { layers: 43, width: 512, window: 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 21, width: 512, ratio: 4, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 20, width: 512, ratio: 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 21, width: 128, ratio: 4, fixedBytes: DSA_INDEXER_BYTES(128) },
    ],
    fp8Kv: true,
    stateBytesPerSeq: dsv4State(21, 20),
    native: { label: "FP4 experts + FP8 (native)", needs: "fp4", bytes: 159_617_149_040 },
    arch: "MoE 256 experts · 512-wide latent: 128-token window + sequence compression (21 layers 4×, 20 layers 128×)",
    repo: "deepseek-ai/DeepSeek-V4-Flash", released: "2026-04-22",
  },
  {
    id: "deepseek-v4-pro", label: "DeepSeek V4 Pro (MoE)", group: "Large MoE",
    params: 1598.84, activeParams: 49, hidden: 7168, layers: 61, vocab: 129280, maxContext: 1048576,
    parts: { mtp: 25.84, experts: 1547.396 }, routing: { experts: 384, active: 6, ffn: 3072 }, mtpBytes: 13956587692,
    kv: [
      { layers: 61, width: 512, window: 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 30, width: 512, ratio: 4, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 31, width: 512, ratio: 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 30, width: 128, ratio: 4, fixedBytes: DSA_INDEXER_BYTES(128) },
    ],
    fp8Kv: true,
    stateBytesPerSeq: dsv4State(30, 31),
    native: { label: "FP4 experts + FP8 (native)", needs: "fp4", bytes: 864_721_029_744 },
    arch: "MoE 384 experts · 512-wide latent: 128-token window + sequence compression (30 layers 4×, 31 layers 128×)",
    repo: "deepseek-ai/DeepSeek-V4-Pro", released: "2026-04-22",
  },
  {
    // CSA2 (README + inference/model.py) : seules les couches 2, 8, 14 et 20 écrivent le KV
    // global et les clés d'indexeur (compression 2× sur 2, 8 et 14 ; aucune sur 20, dont le
    // KV vient de l'encodeur), les autres les relisent ; fenêtre de 128 tokens sur chaque
    // couche. En FP4 natif (échelle par 16 canaux, 32 pour l'indexeur) : 890 octets par
    // token, le chiffre annoncé. Les tables Engram (196 B de paramètres, FP8) restent en
    // mémoire GPU dans l'implémentation de référence. Actifs : 16 B au décodage.
    id: "deepseek-v4.1-flash", label: "DeepSeek V4.1 Flash (MoE)", group: "Large MoE",
    params: 763.21, activeParams: 16, hidden: 5120, layers: 40, vocab: 129280, maxContext: 1048576,
    parts: { mtp: 14.225, vision: 0.001, experts: 543.582, lookup: 196.614 }, routing: { experts: 384, active: 6, ffn: 2304 }, mtpBytes: 7932874632,
    kv: [
      { layers: 40, width: 512, window: 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 3, width: 512, ratio: 2, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 3, width: 128, ratio: 2, fixedBytes: DSA_INDEXER_BYTES(128) },
      { layers: 1, width: 512, fp8Bytes: DSV4_FP8_BYTES },
      { layers: 1, width: 128, fixedBytes: DSA_INDEXER_BYTES(128) },
    ],
    fp8Kv: true,
    stateBytesPerSeq: csa2State(3, 2, 512),
    native: { label: "FP4 experts + FP8 (native)", needs: "fp4", bytes: 510_296_708_312 },
    arch: "MoE 384 experts · global KV written by 4 of 40 layers and shared by the rest (890 bytes per token in native FP4) + 128-token window · 196B Engram memory",
    repo: "deepseek-ai/DeepSeek-V4.1-Flash", released: "2026-09-10",
  },
  {
    // Xiaomi (README) : 10 couches d'attention globale et 60 en fenêtre glissante de 128 tokens,
    // GQA 8 têtes KV, clés de 192 et valeurs de 128. Experts en MXFP4, le reste en FP8 ;
    // encodeurs vision et audio inclus. 1,02 T, 42 B actifs. Le brouillon MTP (dflash/) n'est
    // pas compté : il ne sert qu'au décodage spéculatif.
    id: "mimo-v2.6-pro", label: "MiMo-V2.6 Pro (MoE)", group: "Large MoE",
    params: 1024.22, activeParams: 42, hidden: 6144, layers: 70, vocab: 152576, maxContext: 1048576,
    parts: { mtp: 1.935, vision: 1.008, experts: 1000.191 }, routing: { experts: 384, active: 8 }, mtpBytes: 2463635712,
    kv: [gqa(10, 8, 192, undefined, 128), gqa(60, 8, 192, 128, 128)],
    native: { label: "MXFP4 experts + FP8 (native)", needs: "fp4", bytes: 566_048_103_896 },
    arch: "MoE 384 experts · 10 global-attention layers + 60 sliding-window layers (128 tokens), GQA 8 KV heads (keys 192, values 128)",
    repo: "XiaomiMiMo/MiMo-V2.6-Pro-RL", released: "2026-09-21",
  },
  {
    // Même architecture en plus petit (README) : 9 couches globales à 4 têtes KV, 39 en fenêtre
    // de 128 tokens à 8 têtes KV. 309 B annoncés (310,8 avec vision et audio), 15 B actifs.
    id: "mimo-v2.6-flash", label: "MiMo-V2.6 Flash (MoE)", group: "Large MoE",
    params: 310.76, activeParams: 15, hidden: 4096, layers: 48, vocab: 152576, maxContext: 1048576,
    parts: { mtp: 0.988, vision: 0.964, experts: 302.795 }, routing: { experts: 256, active: 8 }, mtpBytes: 1189400448,
    kv: [gqa(9, 4, 192, undefined, 128), gqa(39, 8, 192, 128, 128)],
    native: { label: "MXFP4 experts + FP8 (native)", needs: "fp4", bytes: 172_932_505_264 },
    arch: "MoE 256 experts · 9 global-attention layers (4 KV heads) + 39 sliding-window layers (128 tokens, 8 KV heads), keys 192, values 128",
    repo: "XiaomiMiMo/MiMo-V2.6-Flash-RL", released: "2026-09-21",
  },
  {
    // Meta (README) : 816 B + encodeurs vision et audio, 42 B actifs, contexte 1M. config.json protégé :
    // lu sur les miroirs mlx-community et AMD (identiques), cohérent avec le README. 62 couches : une en
    // attention complète pour trois en fenêtre de 2 048 tokens, GQA 16 têtes KV × 64. 256 experts, 8 actifs,
    // calculés dans une largeur réduite de 4 096 (moe_hidden_size). Recensement (copie MXFP4 d'AMD, total
    // identique au checkpoint de Meta) : 2,004 B de vision et audio, 798,864 B d'experts, pas de MTP.
    // Dépôt public le 15 octobre 2026 (créé en privé le 15 septembre). Meta publie aussi le NVFP4 (experts
    // en NVFP4, le reste en BF16), son déploiement de référence : 533 Go, servi depuis son propre dépôt.
    id: "muse-spark-open", label: "Muse Spark Open (MoE)", group: "Large MoE",
    params: 822.04, activeParams: 42, hidden: 8192, layers: 62, vocab: 202048, maxContext: 1048576,
    parts: { vision: 2.004, experts: 798.864 }, routing: { experts: 256, active: 8, ffn: 4096 },
    kv: [gqa(16, 16, 64), gqa(46, 16, 64, 2048)],
    native: { label: "NVFP4 experts + BF16 (native)", needs: "fp4", bytes: 533_011_023_472, repo: "meta-models/Muse-Spark-Open-816B-A42B-NVFP4" },
    arch: "MoE 256 experts · 16 full-attention layers + 46 sliding-window layers (2048 tokens), GQA 16 KV × 64",
    repo: "meta-models/Muse-Spark-Open-816B-A42B", released: "2026-10-15",
  },
  {
    // Reflection AI (README, config.json, en-têtes safetensors) : 501,3 B, 23,4 B actifs, contexte 1M. 52 couches :
    // 3 denses puis 49 MoE (256 experts de 2 560, 8 actifs) ; attention GQA à porte, 8 têtes KV × 128, complète sur
    // 9 couches (5, 11… 47 et 51) et en fenêtre de 512 tokens sur les 43 autres. Pas de MTP (brouillon DFlash2 à
    // part). Dépôt public le 16 octobre 2026 (créé le 7). Reflection publie aussi le FP8 (503 Go, le FP8 du
    // calculateur à 0,4 % près) et le NVFP4 (experts en NVFP4, le reste en BF16) : 294 Go, servi depuis son dépôt.
    id: "beam-1.0", label: "Beam 1.0 501B-A23B (MoE)", group: "Large MoE",
    params: 501.27, activeParams: 23.4, hidden: 5120, layers: 52, vocab: 151936, maxContext: 1048576,
    parts: { experts: 493.25 }, routing: { experts: 256, active: 8, ffn: 2560 },
    kv: [gqa(9, 8, 128), gqa(43, 8, 128, 512)],
    native: { label: "NVFP4 experts + BF16 (native)", needs: "fp4", bytes: 293_634_877_368, repo: "reflection/Beam-1.0-501B-A23B-NVFP4" },
    arch: "MoE 256 experts · 9 full-attention layers + 43 sliding-window layers (512 tokens), gated GQA 8 KV × 128",
    repo: "reflection/Beam-1.0-501B-A23B", released: "2026-10-16",
  },
  {
    id: "kimi-k3", label: "Kimi K3 (MoE)", group: "Large MoE",
    params: 2779.93, activeParams: 104, hidden: 7168, layers: 93, vocab: 163840, maxContext: 1048576,
    parts: { vision: 0.447, experts: 2722.741 }, routing: { experts: 896, active: 16 },
    kv: [mla(24, 512, 64)], stateBytesPerSeq: kdaState(69, 96, 128, 4),
    native: { label: "MXFP4 (native)", needs: "fp4", bytes: 1_560_936_091_448 },
    arch: "MoE 896 experts · MLA on 24 of 93 layers · 69 Kimi Delta Attention layers (~430 MiB state per sequence)",
    repo: "moonshotai/Kimi-K3", released: "2026-06-13",
  },
  {
    // Mistral AI (README, config.json, params.json, en-têtes safetensors du checkpoint FP8, recette vLLM) : poids publiés le
    // 29 octobre 2026 (annoncé le 6 en preview). 72 couches, MLA clairsemée (512 + 64) avec indexeur DSA : 63 couches
    // gardent leur cache, 9 réutilisent celui de la couche 35 (kv_source_layers) ; clés d'indexeur sur 44 couches
    // (indexer_types « full »), FP8 (fp8_kv_cache, fp8_indexer). 128 experts dont 4 actifs plus 1 partagé : 52 B actifs avec
    // embeddings et tête (README : 49 B sans). Encodeur Pixtral de 1,7 B, brouillon DSpark de 2,1 B (compté comme MTP).
    // Checkpoint FP8 de 1 060,6 Go ; NVFP4 publié à part (610 Go, Blackwell). 1M (YaRN). Licence propre (Mistral
    // Ecosystem Open-Weight License).
    id: "mistral-large-4", label: "Mistral Large 4 (MoE)", group: "Large MoE",
    params: 1053.23, activeParams: 52, hidden: 6144, layers: 72, vocab: 204800, maxContext: 1048576,
    parts: { mtp: 2.1, vision: 1.704, experts: 1029.244, odd: 0.064 }, routing: { experts: 128, active: 4, ffn: 6144 }, mtpBytes: 4199938048,
    kv: [{ ...mla(63, 512, 64), fp8Bytes: SPARSE_MLA_FP8_BYTES }, { layers: 44, width: 128, fixedBytes: DSA_INDEXER_BYTES(128) }],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 1_060_554_587_912 },
    arch: "MoE 128 experts + 1 shared · sparse MLA (512 + 64 latent) on 72 layers, cache kept by 63 (9 reuse layer 35's) · indexer keys on 44 · vision encoder",
    repo: "mistralai/Mistral-Large-4-1T-A52B", released: "2026-10-29",
  },
  {
    // Mistral AI (README, params.json, en-têtes du checkpoint FP8 au format Mistral) : architecture de DeepSeek V3, MLA (512 +
    // 64) sur 61 couches, 128 experts dont 4 actifs plus 1 partagé, 3 couches denses ; 675 B dont 41 B actifs (README) ;
    // encodeur de vision de 2,6 B. 256K (README ; params.json en permet 288K). Apache 2.0, sorti le 2 décembre 2025.
    id: "mistral-large-3", label: "Mistral Large 3 (MoE)", group: "Large MoE",
    params: 675.99, activeParams: 41, hidden: 7168, layers: 61, vocab: 131072, maxContext: 262144,
    parts: { vision: 2.57, experts: 653.909 }, routing: { experts: 128, active: 4, ffn: 4096 },
    kv: [mla(61, 512, 64)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 681_499_857_920 },
    arch: "MoE 128 experts + 1 shared · MLA (512 + 64 latent) on 61 layers · vision encoder",
    repo: "mistralai/Mistral-Large-3-675B-Instruct-2512", released: "2025-12-02", mistralFormat: true,
  },
  {
    // Mistral AI (README, config.json « mistral4 », en-têtes safetensors) : MLA (256 + 64) sur 36 couches, 128 experts dont 4
    // actifs plus 1 partagé ; 119 B dont 6,5 B actifs. 256K (README ; config.json va jusqu'à 1M). Checkpoint FP8, encodeur de
    // vision de 0,43 B. Apache 2.0, sorti le 16 mars 2026.
    id: "mistral-small-4", label: "Mistral Small 4 119B (MoE)", group: "Large MoE",
    params: 119.4, activeParams: 6.5, hidden: 4096, layers: 36, vocab: 131072, maxContext: 262144,
    parts: { vision: 0.428, experts: 115.964 }, routing: { experts: 128, active: 4, ffn: 2048 },
    kv: [mla(36, 256, 64)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 120_922_770_560 },
    arch: "MoE 128 experts + 1 shared · MLA (256 + 64 latent) on 36 layers · vision encoder",
    repo: "mistralai/Mistral-Small-4-119B-2603", released: "2026-03-16",
  },
  {
    // Démonstrateur de preuves en Lean 4, sur l'architecture de Mistral Small 4 (params.json identique, en-têtes du checkpoint
    // FP8 au format Mistral) ; 119 B dont 6,5 B actifs, 256K (README). Apache 2.0, sorti le 2 juillet 2026.
    id: "leanstral-1.5", label: "Leanstral 1.5 119B-A6B (MoE)", group: "Large MoE",
    params: 119.4, activeParams: 6.5, hidden: 4096, layers: 36, vocab: 131072, maxContext: 262144,
    parts: { vision: 0.428, experts: 115.964 }, routing: { experts: 128, active: 4, ffn: 2048 },
    kv: [mla(36, 256, 64)],
    native: { label: "FP8 (native)", needs: "fp8", bytes: 120_922_788_992 },
    arch: "Lean 4 prover · MoE 128 experts + 1 shared · MLA (256 + 64 latent) on 36 layers",
    repo: "mistralai/Leanstral-1.5-119B-A6B", released: "2026-07-02", mistralFormat: true,
  },
  {
    // Mistral AI (config.json « mixtral ») : 56 couches, 8 têtes KV × 128, 8 experts dont 2 actifs ; 141 B dont 39 B actifs,
    // 64K. Apache 2.0, sorti le 17 avril 2024.
    id: "mixtral-8x22b", label: "Mixtral 8x22B (MoE)", group: "Large MoE",
    params: 140.63, activeParams: 39, hidden: 6144, layers: 56, vocab: 32768, maxContext: 65536,
    parts: { experts: 135.291 }, routing: { experts: 8, active: 2, ffn: 16384 },
    kv: [gqa(56, 8, 128)],
    arch: "MoE 8 experts (2 active) · 56 layers, GQA 8 KV heads × 128",
    repo: "mistralai/Mixtral-8x22B-Instruct-v0.1", released: "2024-04-17",
  },

  // Modèles de code Qwen.
  {
    id: "qwen3-coder-next", label: "Qwen3-Coder-Next 80B-A3B (MoE)", group: "Coding models",
    params: 79.67, activeParams: 3, hidden: 2048, layers: 48, vocab: 151936, maxContext: 262144,
    parts: { experts: 77.309 }, routing: { experts: 512, active: 10 },
    kv: [gqa(12, 2, 256)], stateBytesPerSeq: gdnState(36, 16, 32, 128, 128, 4),
    arch: "MoE 512 experts · KV on 12 of 48 layers (GQA, 2 KV heads × 256) · 36 Gated DeltaNet layers",
    repo: "Qwen/Qwen3-Coder-Next", released: "2026-01-30",
  },
  {
    id: "qwen3-coder-30b-a3b", label: "Qwen3-Coder 30B-A3B (MoE)", group: "Coding models",
    params: 30.53, activeParams: 3.3, hidden: 2048, layers: 48, vocab: 151936, maxContext: 262144,
    parts: { experts: 28.991 }, routing: { experts: 128, active: 8 },
    kv: [gqa(48, 4, 128)],
    arch: "MoE 128 experts · 48 layers, full attention (GQA, 4 KV heads × 128)",
    repo: "Qwen/Qwen3-Coder-30B-A3B-Instruct", released: "2025-07-31",
  },
  {
    id: "qwen3-coder-480b-a35b", label: "Qwen3-Coder 480B-A35B (MoE)", group: "Coding models",
    params: 480.15, activeParams: 35, hidden: 6144, layers: 62, vocab: 151936, maxContext: 262144,
    parts: { experts: 468.084 }, routing: { experts: 160, active: 8 },
    kv: [gqa(62, 8, 128)],
    arch: "MoE 160 experts · 62 layers, full attention (GQA, 8 KV heads × 128)",
    repo: "Qwen/Qwen3-Coder-480B-A35B-Instruct", released: "2025-07-22",
  },
];

// ── Noms des modèles publiés par la veille ──────────────────────────────────
// Le nom vient du dépôt (« gemma-4-E4B-it », « NVIDIA-Nemotron-3-Nano-4B ») : chaque famille prend
// l'écriture de ses presets vérifiés à la main (« Gemma 4 12B » → « Gemma 4 E4B », « Kimi K2.6 » →
// « Kimi K2.5 », « GLM-5.3 » garde son tiret) et un nom qui répète l'organisation la perd. Une
// famille sans preset garde le nom de son labo (« Ornith-1.5 », « clef »). Appliqué à l'affichage,
// donc aussi aux modèles déjà publiés, et par la veille aux nouveaux.

/** Famille (premier mot, en minuscules) → son écriture : casse, et ce qui suit le mot (espace,
 *  tiret, ou rien comme « Qwen3 »), d'après la majorité des presets vérifiés. */
const FAMILY_STYLE = (() => {
  const votes = new Map<string, Map<string, number>>();
  for (const m of VERIFIED_MODELS) {
    const x = m.label.match(/^([A-Za-z]+)([ -]?)(?=[A-Za-z0-9])/);
    if (!x) continue;
    const key = x[1].toLowerCase(), style = `${x[1]}|${x[2]}`, tally = votes.get(key) ?? new Map<string, number>();
    tally.set(style, (tally.get(style) ?? 0) + 1);
    votes.set(key, tally);
  }
  return new Map([...votes].map(([key, tally]) => {
    const [name, sep] = [...tally].sort((a, b) => b[1] - a[1])[0][0].split("|");
    return [key, { name, sep }];
  }));
})();

/** Nom affiché d'un modèle publié par la veille (voir plus haut). Sans effet sur les presets vérifiés. */
export function tidyLabel(label: string, repo?: string): string {
  const moe = / \(MoE\)$/.test(label) ? " (MoE)" : "";
  let name = label.replace(/ \(MoE\)$/, "");
  const org = repo?.split("/")[0];
  if (org && name.toLowerCase().startsWith(`${org.toLowerCase()}-`) && name.length > org.length + 3) name = name.slice(org.length + 1);
  const x = name.match(/^([A-Za-z]+)(.*)$/), style = x && FAMILY_STYLE.get(x[1].toLowerCase());
  if (!x || !style) return name + moe;
  let rest = x[2];
  if (style.sep === " ") {
    // Tirets entre les mots → espaces, sauf dans une taille (« 30B-A3B », « 17B-16E »).
    rest = rest.split("-").reduce((acc, part, k) => (k === 0 ? part : acc + (/^(A\d+(\.\d+)?B|\d+E)\b/.test(part) && /\dB$/.test(acc) ? "-" : " ") + part), "");
    // Dépôt tout en minuscules (« deepseek-coder ») : les mots suivants prennent une majuscule.
    if (x[1] === x[1].toLowerCase()) rest = rest.replace(/ ([a-z])(?=[a-z]{2})/g, (_, c: string) => ` ${c.toUpperCase()}`);
  }
  return style.name + rest + moe;
}

// Sorties récentes ajoutées par le serveur (scripts/watch-models.ts, toutes les heures) :
// modèles dont le config.json décrit une architecture que presetFromHf reproduit déjà
// exactement sur les presets ci-dessus. Un preset vérifié à la main a toujours priorité.
const verified = new Set(VERIFIED_MODELS.flatMap((m) => [m.id, m.repo.toLowerCase()]));
/** Contexte minimal d'un modèle publié : les fiches comparent tout à 8K tokens (SHEET_CONTEXT). Un
 *  modèle publié avant cette règle (deepseek-coder 7B v1.5, 4K) reste dans models-auto.json mais
 *  n'est pas affiché ; la veille n'en publie plus. */
export const MIN_CONTEXT = 8192;
/** Même sortie sous un autre nom : suffixe de précision, variante de post-entraînement (Reasoning de Ministral 3, Base de
 *  LFM3), date de mise à jour des poids. Même nombre de paramètres, même VRAM : une seule fiche. */
export const sameRelease = (repo: string) => repo.toLowerCase().replace(/-(bf16|fp8|nvfp4|fp4|mxfp4)$/, "")
  .replace(/-(instruct|thinking|reasoning|base|chat|it|hf)(?=-|$)/g, "").replace(/-\d{4}(?=-|$)/g, "");
const verifiedRelease = new Set(VERIFIED_MODELS.filter((m) => m.repo).map((m) => sameRelease(m.repo)));
/** Fiche publiée par la veille qui double une fiche vérifiée (LFM3-1.7B-Base à côté de LFM3-1.7B, 29 octobre 2026). */
export const shadowedAuto = (m: Pick<ModelPreset, "id" | "repo">) =>
  verified.has(m.id) || verified.has(m.repo.toLowerCase()) || (!!m.repo && verifiedRelease.has(sameRelease(m.repo)));
export const AUTO_MODELS = (AUTO as unknown as ModelPreset[])
  .filter((m) => !shadowedAuto(m) && !(m.maxContext < MIN_CONTEXT))
  .map((m) => ({ ...m, label: tidyLabel(m.label, m.repo) }));
export const MODELS: ModelPreset[] = [...VERIFIED_MODELS, ...AUTO_MODELS];

// L'origine des modèles (labo, pays) vit dans labs.ts et catalog.ts : voir labOf.

/** Balise « New » : sorti depuis 30 jours au plus. `today` (AAAA-MM-JJ) vient des données
 *  du build, pas de l'horloge : le HTML prérendu et l'hydratation donnent le même résultat. Ces
 *  données (relevé des prix) peuvent dater de la veille : une sortie postérieure est nouvelle aussi
 *  (Muse Spark Open, sorti le 15 octobre 2026, avec un relevé du 14). */
export const NEW_DAYS = 30;
export function isNewModel(m: ModelPreset, today: string): boolean {
  if (!m.released) return false;
  const days = (Date.parse(today) - Date.parse(m.released)) / 86_400_000;
  return days <= NEW_DAYS;
}

export const CUSTOM_ID = "custom";

/** Modèle dense « maison » : GQA simple sur toutes les couches. */
export function customModel(c: {
  params: number; layers: number; kvHeads: number; headDim: number; hidden: number; vocab: number;
}): ModelPreset {
  return {
    id: CUSTOM_ID, label: "Custom", group: "Custom",
    params: c.params, hidden: c.hidden, layers: c.layers, vocab: c.vocab, maxContext: Infinity,
    kv: [gqa(c.layers, c.kvHeads, c.headDim)],
    arch: `${c.layers} layers · GQA, ${c.kvHeads} KV heads × ${c.headDim}`,
    repo: "",
  };
}

/** Dépôt que sert la commande de déploiement : celui du checkpoint natif s'il est à part. */
export const servedRepo = (m: Pick<ModelPreset, "repo" | "native">, formatId: string) =>
  (formatId === "native" && m.native?.repo) || m.repo;


/** Preset d'un dépôt Hugging Face : le sien, celui dont c'est le checkpoint natif (le NVFP4 de Muse Spark Open, le 24GB de
 *  Nemotron 3.5 Bolt), ou la même sortie sous un autre nom (DeepSeek-V4.1-Flash-1017, mise à jour des poids de V4.1 Flash).
 *  Sert aux listes de tendances : chaque modèle y mène à sa fiche. */
export function presetForRepo(repo: string): ModelPreset | undefined {
  const r = repo.toLowerCase(), key = sameRelease(repo);
  return MODELS.find((m) => m.repo?.toLowerCase() === r || m.native?.repo?.toLowerCase() === r)
    ?? MODELS.find((m) => !!m.repo && m.repo.split("/")[0].toLowerCase() === r.split("/")[0] && sameRelease(m.repo) === key);
}

/** Modèle que télécharge `ollama run <famille>` sans précision de taille (tag « latest », relevé sur ollama.com le
 *  20 octobre 2026) : la fiche de chaque ligne de « Popular on Ollama ». */
export const OLLAMA_DEFAULT: Record<string, string> = {
  "llama3.1": "llama-3.1-8b", "llama3.2": "llama-3.2-3b", "llama3": "llama-3-8b", "qwen2.5": "qwen2.5-7b", "qwen3": "qwen3-8b",
  "qwen3.5": "qwen3.5-9b", "qwen2.5-coder": "qwen2.5-coder-7b", "gemma3": "gemma-3-4b", "gemma2": "gemma-2-9b", "gemma4": "gemma-4-e4b",
  "mistral": "mistral-7b-v0.3", "phi3": "phi-3-mini-128k", "llava": "llava-1.6-mistral-7b", "gpt-oss": "gpt-oss-20b",
  "deepseek-r1": "deepseek-r1-qwen3-8b",
};
